import "./style.css";
import { exportNotice } from "./export-notice.js";
import { createVersionWaveformLoader, decibelsToGain, decodeWaveformPayload, isPlaybackAbortError, loudnessMatchDb } from "./audition.js";
import { activeLyricIndex, assignAnnotationLanes, clamp, commentsMissingReason, comparisonVersions, engineerVisibleComments, isGlobalComment, isPendingProject, isReviewSupplementOpen, normalizeViewport, paragraphBounds, shouldDrawAnnotationStem, shouldUsePointMarker, sortCommentsByAudioTime, sortProjectsForWorkbench, timeToX, waveformChannels, workflowStatusLabel, xToTime, zoomAt } from "./timeline.js";

const config = window.__ALE_AUDIO_REVIEW__ || {
  apiBase: "/api/v1",
  proxyPrefix: "",
  devMode: true,
  authMode: "ugos",
  lanBaseUrl: "",
  devUserId: "dev-admin",
  devUserName: "本机管理员",
  devUserType: "admin",
  version: "dev",
};

const statusLabels = {
  draft: "待提交",
  uploaded: "待提交",
  processing: "处理中",
  in_review: "审阅中",
  changes_requested: "需要修改",
  approved: "已批准",
  archived: "已归档",
  superseded: "已替代",
  failed: "处理失败",
};

const roleLabels = { engineer: "音频工程师", reviewer: "监制 / 审核员", admin: "管理员" };

const state = {
  token: "",
  ugosWindow: null,
  me: null,
  users: [],
  engineers: [],
  workflowDefaults: { ready: false, engineer: null, reviewer: null },
  invitations: [],
  notifications: { unread_count: 0, items: [] },
  notificationsOpen: false,
  generatedInviteUrl: "",
  projects: [],
  currentProject: null,
  currentVersion: null,
  waveform: { duration_ms: 0, channel_count: 1, channels: [{ label: "M", peaks: [[0, 0]] }], peaks: [[0, 0]] },
  comparisonEnabled: false,
  comparisonSource: null,
  comparisonWaveform: null,
  comparisonLoadingId: null,
  loudnessMatchEnabled: false,
  loudnessMatchLoadingId: null,
  loudnessMatchError: null,
  auditionVersionId: null,
  selection: { start_ms: 0, end_ms: null },
  viewport: { zoom: 1, start_ms: 0 },
  pointers: new Map(),
  gesture: null,
  lyrics: null,
  lyricSearchResults: [],
  lyricSearchTitle: "",
  lyricSearchArtist: "",
  lyricSelection: null,
  lyricFocusIndex: 0,
  activeLyricIndex: -1,
  lyricSearchBusy: false,
  commentAnnotations: [],
  workspaceSplit: {
    columns: readStoredRatio("ale-review-lyrics-width", .5),
    rows: readStoredRatio("ale-review-upper-height", .53),
  },
  page: "projects",
  search: "",
  statusFilter: "all",
  uploading: false,
  uploadMode: "new",
  newProjectFile: null,
  newProjectReferenceFile: null,
  pendingNewProjectId: null,
  shareVersionId: null,
  pendingCommentIds: new Set(),
  previousResultsOpen: false,
  nasScans: { task: null, review: null, reference: null },
  nasBusy: false,
  projectFilesOpen: false,
  audioAnalysisLoadingId: null,
  audioAnalysisError: null,
};

let audio = new Audio();
let standbyAudio = new Audio();
[audio, standbyAudio].forEach((media) => { media.preload = "metadata"; });
let resizeObserver = null;
let diagnosticQueue = readPendingDiagnostics();
let diagnosticFlushTimer = null;
let diagnosticFlushInFlight = false;
let notificationPollTimer = null;
let comparisonWaveformRequestId = 0;
let playbackAttemptId = 0;
let playbackPending = false;
let audioSourceChangeId = 0;
let audioSwitchRequestId = 0;
let crossfadeFrame = null;
let auditionVolume = 1;
let pendingMetadataRestore = null;
let audioContext = null;
const audioGainNodes = new Map();
const INITIAL_WAVEFORM_PEAKS = 1024;
const loadPreviewWaveform = createVersionWaveformLoader((source) => {
  const rawUrl = source.waveform_url || `${config.apiBase}/versions/${source.id}/waveform`;
  const url = new URL(rawUrl, window.location.origin);
  url.searchParams.set("max_peaks", String(INITIAL_WAVEFORM_PEAKS));
  url.searchParams.set("compact", "1");
  return api(url.toString()).then(decodeWaveformPayload);
});
const loadFullWaveform = createVersionWaveformLoader((source) => {
  const rawUrl = source.waveform_url || `${config.apiBase}/versions/${source.id}/waveform`;
  const url = new URL(rawUrl, window.location.origin);
  url.searchParams.delete("max_peaks");
  url.searchParams.delete("compact");
  return api(url.toString());
});

function isEditableElement(element) {
  return element instanceof HTMLElement
    && (element.matches("input, textarea, select") || element.isContentEditable);
}

function releaseEditorFocus(target) {
  const active = document.activeElement;
  if (!isEditableElement(active) || active === target || active.contains(target)) return;
  active.blur();
}

document.addEventListener("pointerdown", (event) => releaseEditorFocus(event.target), true);

function esc(value = "") {
  return String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function initials(name = "") {
  return [...String(name).trim()].slice(0, 2).join("").toUpperCase() || "AR";
}

function formatTime(milliseconds = 0, includeMillis = false) {
  const safe = Math.max(0, Number(milliseconds) || 0);
  const minutes = Math.floor(safe / 60000);
  const seconds = Math.floor((safe % 60000) / 1000);
  const millis = Math.floor(safe % 1000);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}${includeMillis ? `.${String(millis).padStart(3, "0")}` : ""}`;
}

function formatBytes(bytes = 0) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** index).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

function isLanConnection() {
  if (!config.lanBaseUrl) return false;
  try {
    if (new URL(config.lanBaseUrl).origin === window.location.origin) return true;
  } catch { return false; }
  const hostname = window.location.hostname.toLowerCase();
  return window.location.protocol === "http:" && (
    hostname.endsWith(".local")
    || /^10\./.test(hostname)
    || /^192\.168\./.test(hostname)
    || /^172\.(1[6-9]|2\d|3[01])\./.test(hostname)
  );
}

function lanEntryHtml() {
  if (!config.lanBaseUrl || config.authMode !== "invite") return "";
  if (isLanConnection()) {
    return `<span class="lan-connection is-direct" title="音频、波形和文件正在通过 NAS 局域网直连"><i></i>局域网直连</span>`;
  }
  return `<button id="lan-accelerate" class="lan-connection" type="button" title="与 NAS 在同一局域网时使用；切换后音频和波形不再经过公网通道"><i></i><span>局域网加速</span></button>`;
}

function selectionLabel() {
  if (state.lyricSelection) {
    const kind = { word: "歌词字词", line: "歌词句子", paragraph: "歌词段落" }[state.lyricSelection.kind] || "歌词选区";
    return state.lyricSelection.end_ms
      ? `${kind} · ${formatTime(state.lyricSelection.start_ms, true)}–${formatTime(state.lyricSelection.end_ms, true)}`
      : `${kind}点位 · ${formatTime(state.lyricSelection.start_ms, true)}`;
  }
  return state.selection.end_ms
    ? `区间 ${formatTime(state.selection.start_ms, true)}–${formatTime(state.selection.end_ms, true)}`
    : `点位 ${formatTime(state.selection.start_ms, true)}`;
}

function readStoredRatio(key, fallback) {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) && value > 0 ? value : fallback;
  } catch { return fallback; }
}

function commentVisualColor(seed) {
  const value = String(seed ?? "0");
  let hash = 0;
  for (const character of value) hash = (hash * 31 + character.codePointAt(0)) >>> 0;
  const hue = (18 + hash * 137.508) % 360;
  return `hsl(${hue.toFixed(1)} 72% 46%)`;
}

function commentTimeLabel(comment) {
  if (isGlobalComment(comment)) return "全局";
  const end = comment.end_ms !== null && comment.end_ms !== undefined && Number(comment.end_ms) > Number(comment.start_ms)
    ? `–${formatTime(comment.end_ms)}`
    : "";
  return `${formatTime(comment.start_ms)}${end}`;
}

function errorText(payload) {
  const detail = payload?.detail ?? payload ?? "请求失败";
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail)) return detail.map((item) => item.msg).join("；");
  return JSON.stringify(detail);
}

function readPendingDiagnostics() {
  try {
    const stored = JSON.parse(localStorage.getItem("ale-review-pending-diagnostics") || "[]");
    return Array.isArray(stored) ? stored.slice(-100) : [];
  } catch { return []; }
}

function persistPendingDiagnostics() {
  try { localStorage.setItem("ale-review-pending-diagnostics", JSON.stringify(diagnosticQueue.slice(-100))); } catch { /* best effort */ }
}

function diagnosticPath(path) {
  if (path.startsWith("http")) return "external.request";
  return String(path)
    .split("?", 1)[0]
    .replaceAll(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":id")
    .slice(0, 120);
}

function serverTimingMilliseconds(response) {
  const timing = response?.headers?.get("Server-Timing") || "";
  const match = timing.match(/(?:^|,)\s*app;dur=([0-9.]+)/i);
  return match ? Number(match[1]) : null;
}

function recordClientDiagnostic(event) {
  diagnosticQueue.push({
    operation: String(event.operation || "unknown").slice(0, 120),
    phase: String(event.phase || "request").slice(0, 40),
    client_ms: Math.max(0, Number(event.client_ms) || 0),
    server_ms: Number.isFinite(event.server_ms) ? event.server_ms : null,
    status: Math.max(0, Number(event.status) || 0),
    bytes: Number.isFinite(event.bytes) ? Math.max(0, event.bytes) : null,
    attempt: Number.isFinite(event.attempt) ? Math.max(0, event.attempt) : null,
    request_id: String(event.request_id || "").slice(0, 80),
    occurred_at: new Date().toISOString(),
  });
  diagnosticQueue = diagnosticQueue.slice(-100);
  persistPendingDiagnostics();
  if (diagnosticQueue.length >= 10) void flushClientDiagnostics();
  else scheduleDiagnosticFlush();
}

function scheduleDiagnosticFlush(delay = 2000) {
  if (diagnosticFlushTimer) return;
  diagnosticFlushTimer = window.setTimeout(() => {
    diagnosticFlushTimer = null;
    void flushClientDiagnostics();
  }, delay);
}

function diagnosticHeaders() {
  const headers = new Headers({ "Content-Type": "application/json" });
  if (state.token) headers.set("Ugreen-Ttk", state.token);
  if (config.devMode) {
    headers.set("X-Dev-User-ID", config.devUserId || "dev-admin");
    headers.set("X-Dev-User-Name", encodeURIComponent(config.devUserName || "本机管理员"));
    headers.set("X-Dev-User-Type", config.devUserType || "admin");
  }
  return headers;
}

async function flushClientDiagnostics() {
  if (diagnosticFlushInFlight || !state.me || !diagnosticQueue.length) return;
  diagnosticFlushInFlight = true;
  const events = diagnosticQueue.slice(0, 50);
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  try {
    const response = await fetch(`${config.apiBase}/diagnostics/events`, {
      method: "POST",
      headers: diagnosticHeaders(),
      credentials: "same-origin",
      cache: "no-store",
      keepalive: true,
      body: JSON.stringify({
        events,
        context: {
          app_version: config.version || "",
          effective_type: connection?.effectiveType || "",
          rtt_ms: Number.isFinite(connection?.rtt) ? connection.rtt : null,
          downlink_mbps: Number.isFinite(connection?.downlink) ? connection.downlink : null,
          language: navigator.language || "",
          user_agent: navigator.userAgent || "",
        },
      }),
    });
    if (!response.ok) throw new Error(`diagnostics ${response.status}`);
    diagnosticQueue.splice(0, events.length);
    persistPendingDiagnostics();
  } catch {
    scheduleDiagnosticFlush(15000);
  } finally {
    diagnosticFlushInFlight = false;
    if (diagnosticQueue.length) scheduleDiagnosticFlush(5000);
  }
}

async function api(path, options = {}) {
  const url = path.startsWith("http") ? path : `${config.apiBase}${path}`;
  const {
    diagnosticName,
    diagnosticPhase = "request",
    diagnosticBytes,
    diagnosticAttempt,
    ...fetchOptions
  } = options;
  const headers = new Headers(options.headers || {});
  const requestId = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  headers.set("X-Request-ID", requestId);
  if (state.token) headers.set("Ugreen-Ttk", state.token);
  if (config.devMode) {
    headers.set("X-Dev-User-ID", config.devUserId || "dev-admin");
    headers.set("X-Dev-User-Name", encodeURIComponent(config.devUserName || "本机管理员"));
    headers.set("X-Dev-User-Type", config.devUserType || "admin");
  }
  let body = options.body;
  if (body && !(body instanceof Blob) && !(body instanceof ArrayBuffer) && typeof body !== "string") {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(body);
  }
  const started = performance.now();
  let response = null;
  try {
    response = await fetch(url, { cache: "no-store", ...fetchOptions, body, headers, credentials: "same-origin" });
    if (!response.ok) {
      const responseBody = await response.text();
      let payload = responseBody;
      try { payload = JSON.parse(responseBody); } catch { /* keep plain text */ }
      const error = new Error(errorText(payload));
      error.status = response.status;
      error.payload = payload;
      throw error;
    }
    if (response.status === 204 || fetchOptions.method === "HEAD") return response;
    const type = response.headers.get("content-type") || "";
    return type.includes("application/json") ? await response.json() : await response.text();
  } finally {
    recordClientDiagnostic({
      operation: diagnosticName || `${fetchOptions.method || "GET"} ${diagnosticPath(path)}`,
      phase: diagnosticPhase,
      client_ms: performance.now() - started,
      server_ms: serverTimingMilliseconds(response),
      status: response?.status || 0,
      bytes: diagnosticBytes,
      attempt: diagnosticAttempt,
      request_id: response?.headers?.get("X-Request-ID") || requestId,
    });
  }
}

async function directUploadRequest(session, { method, offset = 0, body = null, diagnosticName, diagnosticBytes, diagnosticAttempt }) {
  const url = new URL(session.direct_upload_url, window.location.origin).toString();
  const headers = new Headers({
    "X-Upload-Token": session.direct_upload_token,
    "X-Request-ID": globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  });
  if (method === "PATCH") {
    headers.set("Upload-Offset", String(offset));
    headers.set("Content-Type", "application/offset+octet-stream");
  }
  const started = performance.now();
  let response = null;
  try {
    response = await fetch(url, { method, headers, body, cache: "no-store", credentials: "omit", mode: "cors" });
    if (!response.ok) {
      const responseBody = await response.text();
      let payload = responseBody;
      try { payload = JSON.parse(responseBody); } catch { /* keep plain text */ }
      const error = new Error(errorText(payload));
      error.status = response.status;
      error.payload = payload;
      throw error;
    }
    return response;
  } finally {
    recordClientDiagnostic({
      operation: diagnosticName || `direct-upload.${method.toLowerCase()}`,
      phase: "upload",
      client_ms: performance.now() - started,
      server_ms: serverTimingMilliseconds(response),
      status: response?.status || 0,
      bytes: diagnosticBytes,
      attempt: diagnosticAttempt,
      request_id: response?.headers?.get("X-Request-ID") || headers.get("X-Request-ID"),
    });
  }
}

async function initIdentity() {
  if (!config.devMode && config.authMode === "ugos") {
    const [coreModule, windowModule] = await Promise.all([
      import("@ugreen-nas/core"),
      import("@ugreen-nas/core/cloudWindow"),
    ]);
    const UGOSCore = coreModule.default;
    state.ugosWindow = windowModule.default;
    UGOSCore.init();
    const info = await state.ugosWindow.useCapacity("getThirdToken");
    state.token = info?.third_token || "";
    if (!state.token) throw new Error("未取得 UGOS 登录令牌，请从 NAS 桌面重新打开应用。");
  }
  state.me = await api("/me");
  scheduleDiagnosticFlush(250);
}

window.addEventListener("pagehide", () => { void flushClientDiagnostics(); });

async function refreshProjects() {
  state.projects = await api("/projects");
  if (state.currentProject) {
    const matching = state.projects.find((project) => project.id === state.currentProject.id);
    if (!matching) state.currentProject = null;
  }
}

async function refreshUsers() {
  if (state.me?.role === "admin") state.users = await api("/users");
}

async function refreshEngineers() {
  if (["admin", "reviewer"].includes(state.me?.role)) state.engineers = await api("/engineers");
}

async function refreshNotifications() {
  if (config.authMode === "calendar") {
    state.me = await api("/me");
    const name = document.querySelector(".user-meta strong");
    const avatar = document.querySelector(".user-chip .avatar");
    if (name) name.textContent = state.me.username;
    if (avatar) avatar.textContent = initials(state.me.username);
  }
  state.notifications = await api("/notifications");
}

async function refreshWorkflowDefaults() {
  state.workflowDefaults = await api("/workflow-defaults");
}

async function refreshInvitations() {
  if (state.me?.role === "admin" && config.authMode === "invite") {
    state.invitations = await api("/invitations");
  }
}

function toast(message, kind = "info") {
  const stack = document.querySelector(".toast-stack");
  if (!stack) return;
  const item = document.createElement("div");
  item.className = `toast ${kind}`;
  item.textContent = message;
  stack.append(item);
  setTimeout(() => item.remove(), 4200);
}

function syncNotificationButton() {
  const button = document.querySelector("#notification-toggle");
  if (!button) return;
  button.classList.toggle("has-unread", Boolean(state.notifications.unread_count));
  let badge = button.querySelector("b");
  if (state.notifications.unread_count) {
    if (!badge) {
      badge = document.createElement("b");
      button.append(badge);
    }
    badge.textContent = String(Math.min(99, state.notifications.unread_count));
  } else badge?.remove();
}

function startNotificationPolling() {
  if (notificationPollTimer) return;
  const poll = async () => {
    if (document.visibilityState === "hidden" || !state.me) return;
    const previous = Number(state.notifications.unread_count || 0);
    try {
      await refreshNotifications();
      syncNotificationButton();
      if (state.notifications.unread_count > previous) {
        await refreshProjects();
        toast("收到新的任务提醒");
      }
    } catch { /* 下一轮自动重试 */ }
  };
  notificationPollTimer = window.setInterval(poll, 30000);
  window.addEventListener("focus", poll);
}

function identityChipHtml() {
  const content = `<span class="avatar">${esc(initials(state.me.username))}</span><div class="user-meta"><strong>${esc(state.me.username)}</strong><span>${esc(roleLabels[state.me.role])}</span></div>`;
  if (config.authMode !== "calendar") return `<div class="user-chip">${content}</div>`;
  return `<details class="identity-menu"><summary class="user-chip" aria-label="账户与工作日历" title="账户与工作日历">${content}</summary><div class="identity-actions"><span>${esc(state.me.username)}</span><a href="${esc(config.identityUrl)}">工作日历 / 账号设置</a><button id="identity-logout" type="button">退出统一登录</button></div></details>`;
}

function shellHtml() {
  const isAdmin = state.me.role === "admin";
  const reviewLabel = state.me.role === "engineer" ? "修改与提交" : "波形审阅";
  return `
    <div class="shell">
      <section class="workspace">
        <header class="topbar">
        <div class="brand">
          <img class="ale-wave-mark" src="${esc(config.proxyPrefix)}/ale-mark.png" alt="ALE STUDIO">
          <div class="brand-copy"><strong>ALE STUDIO</strong><span>AUDIO REVIEW</span></div>
        </div>
        <nav class="nav" aria-label="主导航">
          ${navButton("projects", "01", "项目工作台")}
          ${navButton("review", "02", reviewLabel)}
          ${isAdmin ? navButton("admin", "03", "成员与权限") : ""}
        </nav>
          <div class="top-context" aria-hidden="true"></div>
          <div class="user-area">${lanEntryHtml()}${state.me.role === "engineer" ? `<div class="diagnostic-live" title="性能诊断会自动保存到 NAS，管理员可直接查看；不记录音频、批注正文或登录密钥。"><i></i>诊断记录中</div>` : ""}<div class="notification-wrap"><button id="notification-toggle" class="notification-button ${state.notifications.unread_count ? "has-unread" : ""}" type="button" aria-label="提醒" aria-expanded="${state.notificationsOpen}"><span aria-hidden="true">◉</span>${state.notifications.unread_count ? `<b>${Math.min(99, state.notifications.unread_count)}</b>` : ""}</button>${state.notificationsOpen ? notificationsHtml() : ""}</div>${identityChipHtml()}</div>
        </header>
        <main id="page-content"></main>
      </section>
    </div>
    ${dialogsHtml()}
    <div class="toast-stack" aria-live="polite"></div>
  `;
}

function notificationsHtml() {
  const items = state.notifications.items || [];
  return `<section class="notification-popover" aria-label="工作台提醒"><div class="notification-head"><strong>任务提醒</strong>${state.notifications.unread_count ? `<button id="notifications-read-all" type="button" class="text-button">全部已读</button>` : ""}</div><div class="notification-list">${items.length ? items.map((item) => `<button type="button" class="notification-item ${item.read_at ? "is-read" : "is-unread"}" data-notification="${item.id}" data-notification-project="${item.project_id || ""}"><span>${esc(item.title)}</span><small>${esc(item.body)}</small><time>${formatDate(item.created_at)}</time></button>`).join("") : `<div class="notification-empty">暂时没有新提醒</div>`}</div></section>`;
}

function navButton(page, number, label) {
  return `<button class="nav-button ${state.page === page ? "active" : ""}" data-nav="${page}"><span class="nav-icon">${number}</span><span>${label}</span></button>`;
}

function dialogsHtml() {
  const isEngineer = state.me.role === "engineer";
  const reviewerName = state.workflowDefaults.reviewer?.username || "管理员";
  const replacingVersion = state.uploadMode === "replace" && state.currentVersion;
  const replacementLabel = replacingVersion ? `V${String(state.currentVersion.version_number).padStart(2, "0")}` : "当前轮次";
  return `
    ${state.me.role === "admin" ? `<dialog id="deleted-projects-dialog" aria-label="已删除项目"><div class="dialog-head"><h2>已删除项目</h2><button type="button" class="close" data-close aria-label="关闭">×</button></div><div class="dialog-body"><p>文件、版本和批注均保留。恢复后项目重新出现，未过期且未撤销的交付链接恢复可用。</p><div id="deleted-projects-list" class="deleted-projects-list"></div></div></dialog>` : ""}
    ${state.currentProject ? `<dialog id="project-files-dialog" class="project-files-dialog" aria-label="${esc(state.currentProject.title)}项目文件与任务流转">${projectFileWorkflowHtml(state.currentProject)}<button type="button" class="close project-files-dialog-close" data-close aria-label="关闭项目文件">×</button></dialog>` : ""}
    <dialog id="project-dialog" class="project-dialog">
      <form id="project-form">
        <div class="dialog-head"><div><span class="kicker">${isEngineer ? "DIRECT REVIEW · SELF SERVICE" : "NEW PROJECT · NAS WORKFLOW"}</span><h2>${isEngineer ? "提交新项目" : "新建项目目录"}</h2></div><button type="button" class="close" data-close>×</button></div>
        <div class="dialog-body"><div class="form-grid">
          <div id="project-audio-drop" class="project-audio-drop" role="button" tabindex="0" aria-label="选择或拖入待审阅音频">
            <span class="project-audio-icon" aria-hidden="true">↥</span>
            <strong id="project-audio-title">拖入音频，或点击选择</strong>
            <span id="project-audio-meta">可选 · WAV / AIFF / FLAC / MP3 / M4A 等</span>
            <input id="project-audio-input" name="audio_file" type="file" accept="audio/*,.wav,.wave,.aiff,.aif,.flac,.mp3,.m4a,.aac,.ogg" hidden>
          </div>
          <div class="field"><label for="project-title">项目名称</label><input id="project-title" name="title" maxlength="160" required placeholder="例如：新单曲《城市微光》混音"></div>
          <div class="field"><label for="client-name">客户 / 艺人</label><input id="client-name" name="client_name" maxlength="160" placeholder="可稍后补充"></div>
          <div class="field"><label for="project-description">工作说明</label><textarea id="project-description" name="description" maxlength="4000" placeholder="交付规格、参考方向、截止时间……"></textarea></div>
          <div class="nas-project-preview"><span>系统将自动建立</span><strong>项目名称 / 任务文件</strong><strong>项目名称 / 待审阅</strong><small>${isEngineer ? "不选文件时只建立目录：把成品放入“待审阅”，回到工作台关联后提交。现在选择文件则在线上传并自动提交监制。" : "不选文件时先建立目录并走原任务派发；现在选择音频则建立后直接进入审阅。"}</small></div>
          <div class="fixed-workflow"><span><i>${isEngineer ? "提交方式" : "任务发派"}</i><strong>${isEngineer ? "NAS 关联或在线上传" : "关联文件后选择工程师"}</strong></span><span><i>监制</i><strong>${esc(reviewerName)}</strong></span><em>${isEngineer ? "无需监制下发" : "NAS 目录驱动"}</em></div>
        </div><div id="project-upload-progress" class="hidden"><div class="progress"><i></i></div><div class="progress-note">准备建立项目…</div></div><div class="dialog-actions"><button type="button" class="button secondary" data-close>取消</button><button class="button" id="project-submit" type="submit">建立项目目录</button></div></div>
      </form>
    </dialog>
    <dialog id="upload-dialog">
      <form id="upload-form">
        <div class="dialog-head"><div><span id="upload-kicker" class="kicker">${replacingVersion ? "DIRECT REPLACE · SAME ROUND" : "DIRECT UPLOAD · AUTO SUBMIT"}</span><h2 id="upload-title">${replacingVersion ? `替换 ${replacementLabel} 音频` : "上传并提交审阅"}</h2></div><button type="button" class="close" data-close>×</button></div>
        <div class="dialog-body"><div class="form-grid">
          <div class="field"><label for="audio-file">音频文件</label><input id="audio-file" name="file" type="file" accept="audio/*,.wav,.aiff,.aif,.flac,.mp3,.m4a,.aac,.ogg" required><small id="upload-help">${replacingVersion ? `只更新 ${replacementLabel} 的音频、波形和技术信息；版本号、批注与歌词保持不变。退回修改中的轮次替换后可重新提交。` : "选择一次即可：支持直达 NAS 断点上传，完成处理后自动提交给监制。"}</small></div>
          <div class="field"><label for="version-notes">${replacingVersion ? "替换说明" : "版本说明"}</label><textarea id="version-notes" name="notes" maxlength="2000" placeholder="${replacingVersion ? "这次替换修正了什么？（可选）" : "本版修改了哪些内容？"}"></textarea></div>
        </div><div id="upload-progress" class="hidden"><div class="progress"><i></i></div><div class="progress-note">准备上传…</div></div><div class="dialog-actions"><button type="button" class="button secondary" data-close>取消</button><button class="button" id="upload-submit" type="submit">${replacingVersion ? `替换 ${replacementLabel}` : "上传并提交审阅"}</button></div></div>
      </form>
    </dialog>
    <dialog id="reference-dialog">
      <form id="reference-form">
        <div class="dialog-head"><div><span class="kicker">PROJECT REFERENCE · OPTIONAL</span><h2>${state.currentProject?.reference ? "替换原唱参考" : "上传原唱参考"}</h2></div><button type="button" class="close" data-close>×</button></div>
        <div class="dialog-body"><div class="form-grid"><div class="field"><label for="reference-file">原唱音频</label><input id="reference-file" name="file" type="file" accept="audio/*,.wav,.wave,.aiff,.aif,.flac,.mp3,.m4a,.aac,.ogg" required><small>作为整个项目的固定参考，不会创建新版本，也不会影响审阅状态。</small></div></div><div id="reference-upload-progress" class="hidden"><div class="progress"><i></i></div><div class="progress-note">准备上传…</div></div><div class="dialog-actions"><button type="button" class="button secondary" data-close>取消</button><button class="button" id="reference-submit" type="submit">${state.currentProject?.reference ? "替换原唱" : "上传原唱"}</button></div></div>
      </form>
    </dialog>
    <dialog id="share-dialog">
      <form id="share-form">
        <div class="dialog-head"><div><span class="kicker">APPROVED DELIVERY</span><h2>生成客户交付链接</h2></div><button type="button" class="close" data-close>×</button></div>
        <div class="dialog-body"><div class="form-grid">
          <div class="field"><label for="expiry-days">链接有效期</label><select id="expiry-days" name="expiry_days"><option value="7">7 天</option><option value="14" selected>14 天</option><option value="30">30 天</option><option value="90">90 天</option><option value="permanent">永久</option></select><small>永久链接没有到期日，但仍可随时撤销。</small></div>
          <div class="field"><label for="share-password">访问密码（可选）</label><input id="share-password" name="password" type="password" maxlength="128" autocomplete="new-password"></div>
          <div class="field"><label for="max-accesses">最多访问次数（可选）</label><input id="max-accesses" name="max_accesses" type="number" min="1" max="10000" placeholder="不填则不限制"></div>
          <label class="check-row"><input name="allow_download" type="checkbox" checked>允许客户下载批准文件</label>
        </div><div id="share-result"></div><div class="dialog-actions"><button type="button" class="button secondary" data-close>关闭</button><button class="button" type="submit">生成链接</button></div></div>
      </form>
    </dialog>
  `;
}

function render() {
  const app = document.querySelector("#app");
  app.innerHTML = shellHtml();
  const content = document.querySelector("#page-content");
  if (state.page === "projects") content.innerHTML = projectsPageHtml();
  else if (state.page === "review") content.innerHTML = reviewPageHtml();
  else content.innerHTML = adminPageHtml();
  bindGlobal();
  if (state.page === "projects") bindProjects();
  if (state.page === "review") bindReview();
  if (state.page === "admin") bindAdmin();
  if (state.projectFilesOpen && state.page === "review" && state.currentProject) {
    document.querySelector("#project-files-dialog")?.showModal();
  }
}

function projectsPageHtml() {
  const isEngineer = state.me.role === "engineer";
  const totals = {
    all: state.projects.length,
    review: state.projects.filter((project) => project.status === "in_review").length,
    changes: state.projects.filter((project) => project.status === "changes_requested").length,
    approved: state.projects.filter((project) => project.status === "approved").length,
  };
  const term = state.search.trim().toLowerCase();
  const filtered = sortProjectsForWorkbench(state.projects.filter((project) => {
    const text = `${project.title} ${project.client_name}`.toLowerCase();
    return (!term || text.includes(term)) && (state.statusFilter === "all" || project.status === state.statusFilter);
  }));
  return `<section class="page">
    <div class="page-head"><div><span class="kicker">WORKBENCH · 01</span><h1 class="page-title">${isEngineer ? "音频工程师工作台" : "ALE音乐审阅交付"}</h1><p class="page-subtitle">${isEngineer ? "接收任务，也可以自主建立项目，通过 NAS 关联或在线上传直接提交监制审阅。" : "建立 NAS 项目目录、关联任务文件、派发工程师并接收返回提醒。"}</p></div><button id="new-project" class="button">${isEngineer ? "＋ 提交新项目" : "＋ 建立新项目"}</button></div>
    <div class="stats">
      ${statCard(totals.all, "全部项目", "你有权限访问的任务")}
      ${statCard(totals.review, isEngineer ? "监制审阅中" : "等待审阅", isEngineer ? "等待监制反馈" : "监制需要处理")}
      ${statCard(totals.changes, isEngineer ? "待修改" : "修改进行中", isEngineer ? "处理批注并上传新版本" : "等待工程师提交新版本")}
      ${statCard(totals.approved, "已批准", "可以生成交付链接")}
    </div>
    <div class="toolbar"><input id="project-search" class="search" type="search" value="${esc(state.search)}" placeholder="搜索项目或客户…"><select id="status-filter" class="filter-select"><option value="all">全部状态</option>${["draft","in_review","changes_requested","approved"].map((status) => `<option value="${status}" ${state.statusFilter === status ? "selected" : ""}>${statusLabels[status]}</option>`).join("")}</select></div>
    ${state.me.role === "admin" ? `<div class="deleted-projects-entry"><button id="show-deleted-projects" class="button small secondary">已删除项目</button></div>` : ""}
    ${filtered.length ? `<div class="project-grid">${filtered.map(projectCardHtml).join("")}</div>` : `<div class="empty"><strong>没有匹配的项目</strong><span>建立第一个任务，或调整筛选条件。</span></div>`}
  </section>`;
}

function statCard(value, label, note) {
  return `<div class="stat-card"><div class="stat-value">${value}</div><div class="stat-label">${label}</div><div class="stat-note">${note}</div></div>`;
}

function projectCardHtml(project) {
  const current = project.versions?.[0] || null;
  const status = current?.status || project.status;
  const cardStatusLabel = current ? workflowStatusLabel(project, current) : project.task_sent_at ? "制作中" : "待派发";
  const approved = project.versions?.find((version) => version.id === project.approved_version_id && version.status === "approved") || null;
  const canManageFlags = ["admin", "reviewer"].includes(state.me.role);
  const hasCardActions = Boolean(approved || canManageFlags);
  const flagBadges = `${project.is_priority ? '<span class="project-flag priority">优先</span>' : ""}${project.is_delivered ? '<span class="project-flag delivered">已交付</span>' : ""}`;
  const flagActions = canManageFlags ? `<button type="button" class="button small project-flag-button priority ${project.is_priority ? "active" : ""}" data-project-flag="priority" data-project-id="${project.id}" data-enabled="${Boolean(project.is_priority)}" aria-pressed="${Boolean(project.is_priority)}">${project.is_priority ? "取消优先" : "标记优先"}</button><button type="button" class="button small project-flag-button delivered ${project.is_delivered ? "active" : ""}" data-project-flag="delivered" data-project-id="${project.id}" data-enabled="${Boolean(project.is_delivered)}" aria-pressed="${Boolean(project.is_delivered)}">${project.is_delivered ? "取消已交付" : "标记已交付"}</button>` : "";
  return `<article class="project-card ${hasCardActions ? "has-card-actions" : ""} ${isPendingProject(project) ? "is-pending" : ""} ${project.is_priority ? "is-priority" : ""} ${project.is_delivered ? "is-delivered" : ""}">
    <button type="button" class="project-card-open" data-project="${project.id}" aria-label="打开项目 ${esc(project.title)}">
      <div class="card-top"><span class="project-code">PRJ-${project.id.slice(0, 6).toUpperCase()}</span><span class="card-status-stack"><span class="project-flags">${flagBadges}</span><span class="status ${status}">${esc(cardStatusLabel)}</span></span></div>
      <h3>${esc(project.title)}</h3><div class="project-client">${esc(project.client_name || "未填写客户")}</div>
      ${projectAssigneesHtml(project, "project-assignees")}
      <div class="project-metrics"><span><strong>${current ? `V${String(current.version_number).padStart(2, "0")}` : "—"}</strong> 当前版本</span><span><strong>${project.task_files?.length || 0}</strong> 任务文件</span><span>${formatDate(project.updated_at)}</span></div>
    </button>
    ${hasCardActions ? `<div class="project-card-actions">${flagActions}${approved ? `<button type="button" class="button small" data-share-version="${approved.id}">生成交付链接</button><a class="button small secondary" href="${esc(approved.original_url)}">下载定稿</a>` : ""}${state.me.role === "admin" ? `<button type="button" class="button small danger" data-delete-project="${project.id}" aria-label="删除项目 ${esc(project.title)}">删除项目</button>` : ""}</div>` : ""}
  </article>`;
}

function projectAssigneesHtml(project, className = "review-assignees") {
  const engineer = project.assigned_engineer?.username || "未指定";
  const reviewer = project.reviewer?.username || "未指定";
  return `<div class="${className}" title="工程师：${esc(engineer)}；监制：${esc(reviewer)}"><span><i>工程师</i>${esc(engineer)}</span><span><i>监制</i>${esc(reviewer)}</span></div>`;
}

function linkedFileListHtml(files, emptyText, showDownload = true) {
  if (!files?.length) return `<div class="nas-file-empty">${esc(emptyText)}</div>`;
  return `<div class="linked-file-list">${files.map((file) => `<div class="linked-file"><span><strong>${esc(file.filename)}</strong><small>${formatBytes(file.size_bytes)} · ${formatDate(file.created_at)}</small></span>${showDownload ? `<a class="text-button" href="${esc(file.download_url)}">下载</a>` : `<i>已关联</i>`}</div>`).join("")}</div>`;
}

function nasScanCandidatesHtml(kind) {
  return `<div data-nas-output="${kind}">${nasScanInnerHtml(kind)}${nasScanTimeHtml(state.nasScans[kind])}</div>`;
}

function nasScanTimeHtml(scan) {
  return scan?.checkedAt ? `<small class="nas-format-note">扫描于 ${esc(new Date(scan.checkedAt).toLocaleTimeString())} · ${scan.files.length} 个文件</small>` : "";
}

function nasScanInnerHtml(kind) {
  const scan = state.nasScans[kind];
  if (!scan) return `<div class="nas-scan-hint">点击扫描，读取 NAS 文件夹中的最新文件。</div>`;
  const available = scan.files.filter((file) => !file.linked && (kind === "task" || file.supported_audio));
  const blocked = scan.files.filter((file) => !file.linked && kind === "review" && !file.supported_audio);
  if (!scan.files.length) return `<div class="nas-scan-hint">云端暂未收到文件。${scan.waiting ? "正在每 10 秒自动重试（最多 3 分钟）" : "请确认 NAS 文件传输完成后再次扫描"}；大文件同步需要时间，无需重复上传。</div>`;
  return `<div class="nas-candidate-list">${scan.files.map((file) => {
    const selectable = !file.linked && (kind === "task" || file.supported_audio);
    const control = kind === "task"
      ? `<input type="checkbox" name="filename" value="${esc(file.filename)}" ${selectable ? "" : "disabled"}>`
      : `<input type="radio" name="filename" value="${esc(file.filename)}" ${selectable ? "" : "disabled"}>`;
    const label = file.linked ? "已关联" : !file.supported_audio && kind === "review" ? "不支持的格式" : "可关联";
    return `<label class="nas-candidate ${file.linked ? "is-linked" : selectable ? "" : "is-disabled"}">${control}<span><strong>${esc(file.filename)}</strong><small>${formatBytes(file.size_bytes)} · ${formatDate(file.modified_at)}</small></span><i>${label}</i></label>`;
  }).join("")}</div>${kind === "review" && blocked.length ? `<small class="nas-format-note">待审阅目录只会把 WAV、AIFF、FLAC、MP3、M4A 等音频关联为版本。</small>` : ""}${!available.length ? `<small class="nas-format-note">当前没有新的可关联文件。</small>` : ""}`;
}

function nasReferenceCandidatesHtml() {
  return `<div data-nas-output="reference">${nasReferenceInnerHtml()}${nasScanTimeHtml(state.nasScans.reference)}</div>`;
}

function nasReferenceInnerHtml() {
  const scan = state.nasScans.reference;
  if (!scan) return `<div class="nas-scan-hint">选择一个来源目录，读取其中可作为原唱的音频。</div>`;
  const available = scan.files.filter((file) => file.supported_audio);
  if (!scan.files.length) return `<div class="nas-scan-hint">云端暂未收到原唱。${scan.waiting ? "正在每 10 秒自动重试（最多 3 分钟）" : "请等待 NAS 同步后再次扫描"}，无需重复上传。</div>`;
  return `<div class="nas-reference-source"><span>当前来源</span><strong>${esc(scan.relative_directory)}</strong></div><div class="nas-candidate-list">${scan.files.map((file) => {
    const selectable = file.supported_audio;
    return `<label class="nas-candidate ${selectable ? "" : "is-disabled"}"><input type="radio" name="filename" value="${esc(file.filename)}" ${selectable ? "" : "disabled"}><span><strong>${esc(file.filename)}</strong><small>${formatBytes(file.size_bytes)} · ${formatDate(file.modified_at)}</small></span><i>${selectable ? "可关联为原唱" : "不支持的格式"}</i></label>`;
  }).join("")}</div>${available.length ? "" : `<small class="nas-format-note">当前目录没有 WAV、AIFF、FLAC、MP3、M4A 等可关联音频。</small>`}`;
}

function projectFileWorkflowHtml(project) {
  const manager = ["admin", "reviewer"].includes(state.me.role);
  const selfStarted = state.me.role === "engineer" && project.created_by === state.me.nas_user_id && !project.task_sent_at;
  const storage = project.project_storage || { ready: false };
  if (!storage.ready) {
    return `<section class="panel project-file-workflow storage-missing"><div><strong>尚未建立 NAS 项目目录</strong><span>这是升级前建立的项目，可以补建“任务文件”和“待审阅”目录。</span></div>${manager ? `<button id="create-project-storage" class="button small">建立目录</button>` : ""}</section>`;
  }
  const engineerOptions = state.engineers.map((engineer) => `<option value="${esc(engineer.nas_user_id)}" ${project.assigned_engineer_id === engineer.nas_user_id ? "selected" : ""}>${esc(engineer.username)}</option>`).join("");
  const taskStatus = project.task_sent_at
    ? `已于 ${formatDate(project.task_sent_at)} 派发给 ${project.assigned_engineer?.username || "工程师"}`
    : selfStarted ? "工程师自主创建，无需监制派发" : "文件关联完成后即可发送任务";
  const taskControls = manager ? `<form id="task-file-link-form" class="nas-file-form"><div class="nas-section-actions"><button type="button" class="button small secondary" data-scan-nas="task">扫描任务文件</button><button type="submit" class="button small" ${state.nasBusy ? "disabled" : ""}>关联所选</button></div>${nasScanCandidatesHtml("task")}</form><form id="dispatch-task-form" class="dispatch-form"><label><span>指定工程师</span><select name="engineer_id" required>${engineerOptions || `<option value="">暂无可用工程师</option>`}</select></label><button class="button small" type="submit" ${!project.task_files?.length || !engineerOptions || state.nasBusy ? "disabled" : ""}>${project.task_sent_at ? "重新发送任务" : "发送任务"}</button></form>` : `<div class="engineer-task-note"><strong>${selfStarted ? "自主提交项目" : project.task_sent_at ? "任务已接收" : "等待任务派发"}</strong><span>${selfStarted ? "直接把成品放入“待审阅”后关联，或使用页面上的“浏览器上传新版本”。" : project.description ? esc(project.description) : "发派者尚未填写额外说明。"}</span></div>`;
  const canAssociateReview = state.me.role === "engineer" || manager;
  const currentVersion = project.versions?.[0] || null;
  const reviewControls = canAssociateReview ? `<form id="review-file-associate-form" class="nas-file-form"><div class="nas-section-actions"><button type="button" class="button small secondary" data-scan-nas="review">扫描待审阅</button><button type="submit" class="button small" data-associate-mode="new" ${state.nasBusy ? "disabled" : ""}>关联为新版本</button>${currentVersion ? `<button type="submit" class="button small secondary" data-associate-mode="replace" ${state.nasBusy ? "disabled" : ""}>替换当前 V${String(currentVersion.version_number).padStart(2, "0")}</button>` : ""}</div>${nasScanCandidatesHtml("review")}<textarea name="notes" maxlength="2000" placeholder="版本或替换说明（可选）"></textarea></form>` : "";
  const reference = project.reference;
  const referenceControls = `<form id="reference-file-associate-form" class="nas-file-form nas-reference-form"><div class="nas-section-actions"><button type="button" class="button small secondary" data-scan-reference="task">扫描任务文件</button><button type="button" class="button small secondary" data-scan-reference="review">扫描待审阅</button><button type="submit" class="button small" ${state.nasBusy ? "disabled" : ""}>关联原唱</button></div>${nasReferenceCandidatesHtml()}</form>`;
  return `<section class="panel project-file-workflow">
    <div class="project-storage-head"><div><span class="kicker">NAS PROJECT FILES</span><h2>项目文件与任务流转</h2></div><div class="storage-path"><span>项目目录</span><strong>${esc(storage.directory_name)}</strong></div></div>
    <div class="project-file-columns">
      <section class="project-file-section"><div class="nas-section-head"><div><b>01</b><span><strong>任务文件</strong><small>${esc(storage.task_directory)}</small></span></div><em>${esc(taskStatus)}</em></div>${linkedFileListHtml(project.task_files, "尚未关联任务文件")}${taskControls}</section>
      <section class="project-file-section"><div class="nas-section-head"><div><b>02</b><span><strong>待审阅</strong><small>${esc(storage.review_directory)}</small></span></div><em>${project.versions?.length ? `已生成 ${project.versions.length} 个审阅版本` : "等待工程师返回文件"}</em></div>${linkedFileListHtml(project.review_source_files, "尚未关联待审阅音频", false)}${reviewControls}</section>
      <section class="project-file-section project-reference-section"><div class="nas-section-head"><div><b>03</b><span><strong>原唱参考</strong><small>从项目 NAS 文件关联 · 不增加版本轮次</small></span></div><em>${reference ? `已关联 ${esc(reference.filename)}` : "尚未关联原唱"}</em></div>${reference ? `<div class="linked-file reference-linked-file"><span><strong title="${esc(reference.filename)}">${esc(reference.filename)}</strong><small>${formatTime(reference.duration_ms)} · ${formatBytes(reference.size_bytes)}</small></span><i>原唱</i></div>` : `<div class="nas-file-empty">可从“任务文件”或“待审阅”选择一份音频</div>`}${referenceControls}</section>
    </div>
  </section>`;
}

function audioMetric(value, suffix, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric.toFixed(1)} ${suffix}` : fallback;
}

function audioSampleRate(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "—";
  return numeric % 1000 === 0 ? `${numeric / 1000} kHz` : `${(numeric / 1000).toFixed(1)} kHz`;
}

function audioBitrate(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "—";
  return numeric >= 1_000_000 ? `${(numeric / 1_000_000).toFixed(2)} Mbps` : `${Math.round(numeric / 1000)} kbps`;
}

function audioAnalysisHtml(version) {
  const loading = state.audioAnalysisLoadingId === version.id;
  const error = state.audioAnalysisError?.versionId === version.id ? state.audioAnalysisError.message : "";
  const analysis = version.audio_analysis;
  const integratedFallback = loading ? "检测中…" : (error || analysis?.measurement_error) ? "响度不可用" : "待检测";
  const integrated = analysis?.silence ? "−∞ LUFS" : audioMetric(analysis?.integrated_lufs, "LUFS", integratedFallback);
  const loudnessRange = audioMetric(analysis?.loudness_range_lu, "LU");
  const truePeak = analysis?.silence ? "−∞ dBTP" : audioMetric(analysis?.true_peak_dbtp, "dBTP");
  const channelLabel = analysis?.channel_layout || (analysis?.channels ? `${analysis.channels} 声道` : "—");
  const bitDepth = analysis?.bit_depth ? `${analysis.bit_depth} bit` : "—";
  const codec = analysis?.codec_label || analysis?.codec || "—";
  const format = analysis?.format_name || "—";
  const note = error || analysis?.measurement_error || (analysis ? `${analysis.measurement_standard || "EBU R128"} · 基于原始音频完整扫描，不是由试听 MP3 或波形估算。` : "首次打开旧版本时会自动检测并保存结果。");
  return `<details class="audio-info" ${error ? "open" : ""}>
    <summary title="查看当前版本的响度与音频信息"><span>音频信息</span><strong>${integrated}</strong><i aria-hidden="true">⌄</i></summary>
    <section class="audio-info-popover">
      <div class="audio-info-head"><div><span class="kicker">AUDIO INSPECTION · V${String(version.version_number).padStart(2, "0")}</span><strong title="${esc(version.filename)}">${esc(version.filename)}</strong></div>${loading ? `<em><i></i>正在扫描原始音频</em>` : ""}</div>
      <div class="audio-loudness-grid"><div><span>综合响度</span><strong>${integrated}</strong><small>Integrated</small></div><div><span>响度范围</span><strong>${loudnessRange}</strong><small>LRA</small></div><div><span>真峰值</span><strong>${truePeak}</strong><small>True Peak</small></div></div>
      <dl class="audio-technical-grid"><div><dt>采样率</dt><dd>${audioSampleRate(analysis?.sample_rate_hz)}</dd></div><div><dt>位深</dt><dd>${bitDepth}</dd></div><div><dt>声道</dt><dd>${esc(channelLabel)}</dd></div><div><dt>编码</dt><dd title="${esc(codec)}">${esc(codec)}</dd></div><div><dt>容器</dt><dd>${esc(format)}</dd></div><div><dt>码率</dt><dd>${audioBitrate(analysis?.bit_rate_bps)}</dd></div></dl>
      <div class="audio-analysis-note ${error ? "is-error" : ""}"><span>${esc(note)}</span>${error ? `<button type="button" class="text-button" data-audio-analysis-retry>重试检测</button>` : ""}</div>
    </section>
  </details>`;
}

function reviewPageHtml() {
  if (!state.currentProject) {
    return `<section class="page"><div class="page-head"><div><span class="kicker">REVIEW · 02</span><h1 class="page-title">波形审阅</h1></div></div><div class="empty"><strong>尚未选择项目</strong><span>请先从项目工作台打开一个任务。</span><button class="button" data-nav="projects">返回项目</button></div></section>`;
  }
  const project = state.currentProject;
  const version = state.currentVersion;
  const versionComments = version ? project.comments.filter((comment) => comment.version_id === version.id) : [];
  const comments = state.me.role === "engineer" ? engineerVisibleComments(project.comments, version?.id) : versionComments;
  const topComments = sortCommentsByAudioTime(comments.filter((comment) => !comment.parent_id));
  const currentTopComments = sortCommentsByAudioTime(versionComments.filter((comment) => !comment.parent_id));
  const canReview = ["reviewer", "admin"].includes(state.me.role);
  const canUpload = ["engineer", "reviewer", "admin"].includes(state.me.role);
  const canSupplement = canReview && isReviewSupplementOpen(project, version);
  const canCompose = canReview && (version?.status === "in_review" || canSupplement);
  const canManageComments = canCompose;
  const unresolvedCount = topComments.filter((comment) => !comment.resolved).length;
  const workflowStatus = version ? workflowStatusLabel(project, version) : "等待首次上传";
  const previousVersionNumber = Math.max(0, Number(version?.version_number || 0) - 1);
  const previousRoundComments = previousVersionNumber
    ? sortCommentsByAudioTime(project.comments.filter((comment) => !comment.parent_id && Number(comment.version_number) === previousVersionNumber))
    : [];
  return `<section class="page review-page">
    ${!version ? `<div class="review-toolbar panel"><div class="review-title"><h1>${esc(project.title)}</h1><span class="status ${project.status}">${workflowStatus}</span></div><div class="button-row"><button id="open-project-files" class="button small secondary project-files-trigger">项目文件</button>${canUpload ? `<button id="open-upload" class="button small secondary">浏览器上传（备用）</button>` : ""}</div></div><div class="empty"><strong>还没有审阅版本</strong><span>工程师完成后，打开“项目文件”，从“待审阅”关联新版本。</span>${canUpload ? `<button id="empty-upload" class="button secondary">浏览器上传（备用）</button>` : ""}</div>` : `
    <div class="review-toolbar panel">
      <div class="review-title"><h1>${esc(project.title)}</h1><span class="status ${version.status}">${esc(workflowStatus)}</span><span class="review-fact">${project.unresolved_comments} 条未解决</span>${projectAssigneesHtml(project)}</div>
      <div class="button-row">${audioAnalysisHtml(version)}<button id="open-project-files" class="button small secondary project-files-trigger">项目文件</button>${canUpload ? `<button id="open-replace-upload" class="button small">浏览器替换当前轮次</button><button id="open-upload" class="button small secondary">浏览器上传新版本</button>` : ""}${versionActionsHtml(version, canReview)}</div>
    </div>
    <div class="review-workspace" style="--lyrics-width:${(state.workspaceSplit.columns * 100).toFixed(1)}%;--upper-height:${(state.workspaceSplit.rows * 100).toFixed(1)}%">
      <div class="upper-workspace">
        ${lyricsPanelHtml(project, currentTopComments)}
        <div class="workspace-resizer column-resizer" data-resizer="columns" role="separator" tabindex="0" aria-orientation="vertical" aria-label="调整歌词与批注宽度" title="拖动调整歌词与批注宽度"><i></i></div>
        <section class="panel comments-panel">
          <div class="comments-body ${canCompose ? "reviewer-compose-mode" : "feedback-mode"} ${canReview && previousVersionNumber ? `has-previous-results ${state.previousResultsOpen ? "previous-results-open" : "previous-results-collapsed"}` : ""}">
            ${canReview && previousVersionNumber ? previousRoundResultsHtml(previousVersionNumber, previousRoundComments, project.comments) : ""}
            <div class="comments-current">${canCompose ? commentComposeHtml(unresolvedCount, canSupplement) : feedbackSummaryHtml(unresolvedCount, topComments.length)}
              <div class="comments-list">${commentsListHtml(topComments, comments, canManageComments)}</div>
            </div>
          </div>
        </section>
      </div>
      <div class="workspace-resizer row-resizer" data-resizer="rows" role="separator" tabindex="0" aria-orientation="horizontal" aria-label="调整上方工作区与走带高度" title="拖动调整上方工作区与走带高度"><i></i></div>
      <section class="panel timeline-panel">
        <div class="wave-toolbar"><span>拖动选区 · Shift 平移 · 双指缩放 · 空格播放</span><div class="zoom-controls"><button class="button small secondary" data-zoom="out" title="缩小波形">−</button><span id="zoom-level" class="mono">${Number(state.viewport.zoom || 1).toFixed(state.viewport.zoom > 9 ? 0 : 1)}×</span><button class="button small secondary" data-zoom="in" title="放大波形">＋</button><button class="button small ghost" data-zoom="fit">全览</button></div></div>
        <div class="wave-wrap"><canvas id="waveform" tabindex="0" aria-label="精确音频波形；拖动选择范围，双指缩放和平移"></canvas><div id="marker-tooltip" class="marker-tooltip" role="tooltip"></div><div class="wave-help"><span id="viewport-range">${formatTime(0, true)}–${formatTime(version.duration_ms, true)}</span><span>${formatTime(version.duration_ms)} TOTAL</span></div></div>
        <div class="transport"><button id="play" class="play-button" aria-label="播放或暂停">${audio.paused ? "▶" : "❚❚"}</button><div id="timecode" class="timecode">${formatTime(audio.currentTime * 1000, true)}</div><div id="selection-chip" class="selection-chip">${selectionLabel()}</div>${auditionSelectorHtml(version)}<div class="transport-spacer"></div><label class="volume-wrap"><span>VOL</span><input id="volume" class="volume" type="range" min="0" max="1" step="0.01" value="${auditionVolume}" aria-label="音量"></label></div>
        ${referenceBarHtml(project, canUpload)}
      </section>
    </div>`}
  </section>`;
}

function lyricsPanelHtml(project, comments = []) {
  const lines = state.lyrics?.lines || [];
  const results = state.lyricSearchResults || [];
  return `<section class="panel lyrics-panel">
    <form id="lyrics-search-form" class="lyrics-search"><div class="lyrics-inline-meta" title="${state.lyrics ? `${esc(state.lyrics.title)} · ${esc(state.lyrics.artist || "未知歌手")} · ${esc(state.lyrics.source)}` : "尚未关联歌词"}"><strong>歌词${state.lyrics ? ` · ${esc(state.lyrics.title)}` : ""}</strong><span>${state.lyrics ? `${esc(state.lyrics.artist || "未知歌手")} · ${esc(state.lyrics.source)}` : "未关联"}</span></div><input name="title" required maxlength="300" value="${esc(state.lyricSearchTitle)}" placeholder="歌名" aria-label="歌名"><input name="artist" maxlength="300" value="${esc(state.lyricSearchArtist)}" placeholder="歌手" aria-label="歌手"><button class="button small" type="submit" ${state.lyricSearchBusy ? "disabled" : ""}>${state.lyricSearchBusy ? "…" : "搜索"}</button></form>
    ${results.length ? `<div class="lyric-results">${results.map((item, index) => `<button type="button" class="lyric-result" data-attach-lyric="${index}"><strong>${esc(item.title)}</strong><span>${esc(item.source)} · ${esc(item.artist || "未知歌手")} · ${esc(item.album || "未知专辑")} · ${formatTime(item.duration_seconds * 1000)} · ${item.line_count} 行</span><i>使用此 LRC</i></button>`).join("")}</div>` : ""}
    ${lines.length ? `<div id="lyric-prompter" class="lyric-prompter" aria-label="与音频同步滚动的歌词"><div class="lyric-scroll-spacer" aria-hidden="true"></div>${lines.map((line, index) => lyricLineHtml(line, index, comments)).join("")}<div class="lyric-scroll-spacer end" aria-hidden="true"></div></div>` : `<div class="lyrics-empty"><strong>还没有关联同步歌词</strong><span>输入歌名在线搜索，选择结果后会保存在当前音频版本中。</span></div>`}
  </section>`;
}

function lyricLineHtml(line, index, comments = []) {
  const selected = state.lyricSelection && index >= state.lyricSelection.startIndex && index <= state.lyricSelection.endIndex;
  return `<div class="lyric-line ${selected ? "is-selected" : ""}" data-lyric-index="${index}"><button type="button" data-lyric-seek="${index}">${formatTime(line.time_ms, true)}</button><span class="lyric-text">${highlightedLyricHtml(line.text, index, comments)}</span></div>`;
}

function highlightedLyricHtml(text, lineIndex, comments) {
  const value = String(text || "");
  const ranges = [];
  comments.forEach((comment, colorIndex) => {
    if (comment.lyric_start_index === null || comment.lyric_start_index === undefined) return;
    const startLine = Number(comment.lyric_start_index);
    const endLine = Number(comment.lyric_end_index ?? comment.lyric_start_index);
    if (!Number.isFinite(startLine) || lineIndex < startLine || lineIndex > endLine) return;
    let start = 0;
    let end = value.length;
    if (comment.lyric_char_start !== null && comment.lyric_char_start !== undefined && lineIndex === startLine) {
      start = Number(comment.lyric_char_start);
    }
    if (comment.lyric_char_end !== null && comment.lyric_char_end !== undefined && lineIndex === endLine) {
      end = Number(comment.lyric_char_end);
    } else if (startLine === endLine && comment.lyric_text) {
      const exact = String(comment.lyric_text).trim();
      const exactStart = value.indexOf(exact);
      if (exactStart >= 0) { start = exactStart; end = exactStart + exact.length; }
    }
    start = clamp(Math.round(start), 0, value.length);
    end = clamp(Math.round(end), start, value.length);
    if (end > start) ranges.push({ start, end, color: commentVisualColor(comment.id || colorIndex), resolved: Boolean(comment.resolved) });
  });
  if (!ranges.length) return esc(value);
  const points = [...new Set([0, value.length, ...ranges.flatMap((range) => [range.start, range.end])])].sort((a, b) => a - b);
  return points.slice(0, -1).map((start, index) => {
    const end = points[index + 1];
    const segment = esc(value.slice(start, end));
    const active = ranges.filter((range) => range.start < end && range.end > start);
    if (!active.length) return segment;
    const stripe = 100 / active.length;
    const stops = active.flatMap((range, rangeIndex) => {
      const from = (rangeIndex * stripe).toFixed(1);
      const to = ((rangeIndex + 1) * stripe).toFixed(1);
      const tint = `color-mix(in srgb, ${range.color} 24%, transparent)`;
      return [`${tint} ${from}%`, `${tint} ${to}%`];
    }).join(",");
    const resolved = active.every((range) => range.resolved) ? " is-resolved" : "";
    return `<mark class="lyric-mark${resolved}" style="--mark-color:${active[0].color};--mark-bg:linear-gradient(90deg,${stops})">${segment}</mark>`;
  }).join("");
}

function versionActionsHtml(version, canReview) {
  const actions = [];
  if (version.status === "uploaded" && ["engineer", "admin"].includes(state.me.role)) {
    const missing = state.me.role === "engineer" ? submissionCommentsMissingReason() : [];
    actions.push(`<button class="button small ${missing.length ? "has-blockers" : ""}" data-action="submit" title="${missing.length ? `${missing.length} 条未解决批注仍需备注原因` : "提交给监制审阅"}">提交审阅${missing.length ? ` · ${missing.length}` : ""}</button>`);
  }
  if (canReview && version.status === "in_review") {
    actions.push(`<button class="button small secondary" data-action="changes">提交给工程师修改</button>`);
    actions.push(`<button class="button small" data-action="approve">批准交付</button>`);
  }
  if (version.status === "approved") actions.push(`<button class="button small" data-action="share">生成交付链接</button>`);
  actions.push(`<a class="button small secondary" href="${esc(version.original_url)}">下载原文件</a>`);
  return actions.join("");
}

function submissionCommentsMissingReason() {
  if (!state.currentProject || state.me.role !== "engineer") return [];
  return commentsMissingReason(state.currentProject.comments, state.me.nas_user_id);
}

function commentComposeHtml(unresolvedCount = 0, supplement = false) {
  return `<div class="comment-compose-stack">
    <form id="global-comment-form" class="comment-compose global-comment-compose" data-comment-form>
      <input type="hidden" name="comment_scope" value="global">
      <div class="comments-inline-meta"><strong>${supplement ? "补充全局批注" : "全局批注"}</strong><span>${supplement ? "仍计本次批注" : "整首作品"}</span></div>
      <textarea name="body" rows="1" maxlength="4000" required placeholder="${supplement ? "补充整首作品的总体意见…" : "输入整首作品的总体意见…"}"></textarea>
      <button class="button small" type="submit">${supplement ? "补充" : "添加"}</button>
    </form>
    <form id="comment-form" class="comment-compose timed-comment-compose" data-comment-form>
      <input type="hidden" name="comment_scope" value="timed">
      <div class="comments-inline-meta"><strong>${supplement ? "补充时间批注" : "时间批注"}</strong><span>${supplement ? `同一批次 · ${unresolvedCount} 未解决` : `${unresolvedCount} 未解决`}</span></div>
      <span id="anchor-label" class="anchor">${selectionLabel()}</span>
      <span class="comment-selection-slot">${state.lyricSelection ? `<blockquote class="lyric-selection-preview" title="${esc(state.lyricSelection.text)}">${esc(state.lyricSelection.text)}</blockquote>` : ""}</span>
      <textarea name="body" rows="1" maxlength="4000" required placeholder="${supplement ? "补充当前位置的修改意见…" : "输入当前位置的修改意见…"}"></textarea>
      <button class="button small" type="submit">${supplement ? "补充" : "添加"}</button>
    </form>
  </div>`;
}

function feedbackSummaryHtml(unresolvedCount, commentCount) {
  if (state.me.role === "engineer") {
    return `<div class="feedback-summary engineer-feedback"><div><strong>修改反馈</strong><span>${commentCount ? "逐条勾选已解决；暂未解决的项目必须填写原因。" : "当前还没有监制批注，上传版本后可提交审阅。"}</span></div><b>${unresolvedCount} 未解决</b></div>`;
  }
  return `<div class="feedback-summary reviewer-waiting"><div><strong>等待工程师提交</strong><span>工程师提交新版本后，这里会恢复全局批注和时间批注输入。</span></div><b>${unresolvedCount} 未解决</b></div>`;
}

function previousRoundResultsHtml(versionNumber, comments, allComments) {
  const resolvedCount = comments.filter((comment) => comment.resolved).length;
  const unresolvedCount = comments.length - resolvedCount;
  const rows = comments.length ? comments.map((comment) => {
    const replies = allComments
      .filter((item) => item.parent_id === comment.id)
      .sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
    const engineerReason = [...replies].reverse().find((reply) => reply.author_role === "engineer");
    const anchor = isGlobalComment(comment) ? "全局" : commentTimeLabel(comment);
    return `<div class="previous-result-row ${comment.resolved ? "is-resolved" : "is-unresolved"}">
      <div class="previous-result-meta"><span class="previous-result-status">${comment.resolved ? "✓ 已处理" : "! 未处理"}</span><span class="previous-result-anchor">${esc(anchor)}</span></div>
      <div class="previous-result-content">${comment.lyric_text ? `<blockquote class="previous-result-lyric">${esc(comment.lyric_text)}</blockquote>` : ""}<p class="previous-result-body">${esc(comment.body)}</p><span class="previous-result-reason">${comment.resolved ? "工程师已标记完成" : engineerReason ? `未处理原因：${esc(engineerReason.body)}` : "未填写未处理原因"}</span></div>
    </div>`;
  }).join("") : `<div class="previous-results-empty">上一轮没有批注。</div>`;
  const open = state.previousResultsOpen;
  return `<section class="previous-results ${open ? "is-open" : "is-collapsed"}" aria-label="上一轮处理结果">
    <button type="button" class="previous-results-toggle" data-toggle-previous-results aria-expanded="${open}" aria-controls="previous-results-list" title="${open ? "收起上一轮处理结果" : "展开上一轮处理结果"}"><span class="previous-results-chevron" aria-hidden="true">${open ? "‹" : "›"}</span><strong>上一轮处理结果</strong><span class="previous-results-version">V${String(versionNumber).padStart(2, "0")}</span><span class="previous-results-summary">${resolvedCount} 已处理 · ${unresolvedCount} 未处理</span></button>
    <div id="previous-results-list" class="previous-results-list" ${open ? "" : "hidden"}>${rows}</div>
  </section>`;
}

function auditionSelectorHtml(currentVersion) {
  const comparisonSources = nonCurrentAuditionSources();
  if (!comparisonSources.length) return "";
  const activeId = state.auditionVersionId || currentVersion.id;
  const comparisonDb = loudnessMatchDb(currentVersion.audio_analysis, state.comparisonSource?.audio_analysis);
  const matchBusy = Boolean(state.loudnessMatchLoadingId);
  const matchLabel = matchBusy
    ? "Match 分析中…"
    : state.loudnessMatchEnabled && comparisonDb !== null
      ? `Match ${comparisonDb >= 0 ? "+" : ""}${comparisonDb.toFixed(1)} dB`
      : "响度 Match";
  return `<div class="audition-selector" aria-label="选择试听与对比源"><span>试听</span><button type="button" data-audition-version="${currentVersion.id}" class="audition-source-button ${activeId === currentVersion.id ? "active" : ""}">当前 V${String(currentVersion.version_number).padStart(2, "0")}</button>${comparisonSources.map((source) => {
    const label = auditionSourceLabel(source);
    const selected = state.comparisonEnabled && state.comparisonSource?.id === source.id;
    return `<button type="button" data-audition-version="${source.id}" class="audition-source-button ${source.isReference ? "reference-source" : ""} ${activeId === source.id ? "active" : ""} ${selected ? "comparison-selected" : ""}" title="${state.comparisonEnabled ? `试听并选用 ${label} 做对比` : `试听 ${esc(source.filename)}`}" aria-pressed="${selected}">${label}</button>`;
  }).join("")}<div class="comparison-controls"><button type="button" class="comparison-master-toggle" data-toggle-comparison aria-pressed="${state.comparisonEnabled}" aria-busy="${Boolean(state.comparisonLoadingId)}">${state.comparisonEnabled ? "✓ 已启用对比" : "启用对比"}</button><button type="button" class="loudness-match-toggle" data-toggle-loudness-match aria-pressed="${state.loudnessMatchEnabled}" aria-busy="${matchBusy}" data-match-db="${comparisonDb ?? ""}" ${state.comparisonEnabled ? "" : "disabled"} title="${state.loudnessMatchError ? esc(state.loudnessMatchError) : "自动调整对比源音量，与当前版本的综合响度一致"}">${matchLabel}</button></div></div>`;
}

function referenceBarHtml(project, canUpload) {
  const reference = project.reference;
  return `<div class="reference-bar"><span class="reference-label">原唱参考</span>${reference ? `<strong title="${esc(reference.filename)}">${esc(reference.filename)}</strong><span class="reference-meta">${formatTime(reference.duration_ms)} · ${formatBytes(reference.size_bytes)}</span>` : `<span class="reference-empty">尚未上传 · 不计入版本号</span>`}${canUpload ? `<button id="open-reference-upload" type="button" class="button small secondary">${reference ? "替换原唱" : "＋ 上传原唱"}</button>` : ""}</div>`;
}

function commentsListHtml(topComments, allComments, canManage) {
  if (!topComments.length) return `<div class="empty compact-empty"><strong>暂无批注</strong><span>${state.me.role === "engineer" ? "提交版本后，监制的反馈会显示在这里。" : "工程师提交版本后，可添加时间批注或全局批注。"}</span></div>`;
  return topComments.map((comment) => commentHtml(comment, allComments, canManage)).join("");
}

function commentHtml(comment, allComments, canManage = false) {
  const replies = allComments.filter((item) => item.parent_id === comment.id).sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  const ownReason = replies.find((reply) => reply.author_id === state.me.nas_user_id);
  const global = isGlobalComment(comment);
  const versionLabel = comment.version_number ? `V${String(comment.version_number).padStart(2, "0")} · ` : "";
  const anchor = global
    ? `<span class="time-anchor global-anchor">${versionLabel}全局</span>`
    : `<button class="time-anchor" data-comment-seek="${comment.id}">${versionLabel}${commentTimeLabel(comment)}</button>`;
  const noteSlot = state.me.role === "engineer" && !comment.resolved && !ownReason
    ? `<div class="comment-note-slot"><button type="button" class="text-button reason-toggle" data-toggle-reason="${comment.id}">备注/回复</button><form class="reason-inline-form" data-reason-form="${comment.id}" hidden><input name="body" maxlength="4000" required aria-label="未解决原因" placeholder="填写未解决原因…"><button class="text-button reason-save" type="submit">保存</button><button class="text-button reason-cancel" type="button" data-cancel-reason="${comment.id}" aria-label="收起未解决原因">×</button></form></div>`
    : canManage
      ? `<div class="comment-note-slot"><button type="button" class="text-button comment-edit-toggle" data-edit-comment="${comment.id}">编辑</button></div>`
      : `<span class="comment-note-slot"></span>`;
  return `<article class="comment comment-card ${comment.resolved ? "resolved" : ""}" data-comment="${comment.id}" style="--comment-color:${commentVisualColor(comment.id)}">
    <div class="comment-card-row">
      ${anchor}
      <span class="comment-lyric-slot">${comment.lyric_text ? `<span class="comment-lyric" title="${esc(comment.lyric_text)}">${esc(comment.lyric_text)}</span>` : ""}</span>
      <span class="comment-body">${comment.supplemented_at ? `<b class="comment-supplement-badge">补充</b>` : ""}${esc(comment.body)}</span>
      ${noteSlot}
      <button class="comment-check ${state.pendingCommentIds.has(comment.id) ? "is-pending" : ""}" data-resolve="${comment.id}" data-value="${comment.resolved ? "0" : "1"}" aria-pressed="${comment.resolved ? "true" : "false"}" title="${comment.resolved ? "重新打开" : "标记为已处理"}" ${state.pendingCommentIds.has(comment.id) ? "disabled" : ""}>✓</button>
      ${canManage ? `<button class="comment-delete" data-delete-comment="${comment.id}" title="删除批注" aria-label="删除批注">×</button>` : ""}
    </div>
    ${canManage ? `<form class="comment-edit-form" data-comment-edit-form="${comment.id}" hidden><textarea name="body" maxlength="4000" required aria-label="编辑批注内容">${esc(comment.body)}</textarea><button class="text-button comment-edit-save" type="submit">保存</button><button class="text-button" type="button" data-cancel-comment-edit="${comment.id}">取消</button></form>` : ""}
    ${replies.length ? `<div class="comment-thread">${replies.map((reply) => `<span class="reply-note">${esc(reply.body)}</span>`).join("")}</div>` : ""}
  </article>`;
}

function adminPageHtml() {
  if (state.me.role !== "admin") return `<section class="page"><div class="error-card">只有管理员可以管理成员角色。</div></section>`;
  const invitePanel = config.authMode === "invite" ? `<section class="invite-panel">
      <div class="invite-copy"><strong>个人入口链接</strong><span>每位成员保存自己的永久入口；换浏览器或外网进入时仍使用同一链接。</span></div>
      <form id="invite-form" class="invite-form">
        <input name="username" maxlength="128" required placeholder="成员显示名称">
        <select name="role"><option value="engineer">音频工程师</option><option value="reviewer">监制 / 审核员</option><option value="admin">管理员</option></select>
        <span class="muted">永久有效 · 可撤销</span>
        <button class="button small" type="submit">生成个人入口</button>
      </form>
      ${state.generatedInviteUrl ? `<div class="invite-result"><input id="invite-url" value="${esc(state.generatedInviteUrl)}" readonly><button id="copy-invite" class="button secondary small" type="button">复制链接</button></div>` : ""}
      ${state.generatedInviteUrl ? `<p class="muted">此链接等同该成员身份，请让本人收藏，不要转发给其他人。</p>` : ""}
      <div class="invite-list">${state.invitations.length ? state.invitations.map((invite) => `<div class="invite-row"><span>${esc(invite.username)}</span><span>${esc(roleLabels[invite.role])}</span><span class="muted">${invite.revoked ? "已撤销" : invite.permanent ? invite.used_at ? "永久入口 · 已使用" : "永久入口 · 待首次使用" : invite.used_at ? "一次性邀请 · 已使用" : "一次性邀请 · 待使用"}</span><span class="invite-actions"><button class="text-button" data-regenerate-invite="${invite.id}" data-invite-name="${esc(invite.username)}">重新生成</button>${!invite.revoked && (invite.permanent || !invite.used_at) ? `<button class="text-button" data-revoke-invite="${invite.id}">撤销</button>` : ""}</span></div>`).join("") : `<span class="muted">暂无个人入口</span>`}</div>
    </section>` : "";
  const subtitle = config.authMode === "calendar" ? "与工作日历共用员工账号、密码和姓名。请在日历管理账号；这里只分配音频审阅角色与访问权限。" : config.authMode === "invite" ? "成员无需另设工作台密码；每个人通过自己的永久入口链接进入。" : "成员第一次从 NAS 桌面打开工作台后会自动出现；这里只分配工作台角色，不创建密码。";
  return `<section class="page"><div class="page-head"><div><span class="kicker">ACCESS CONTROL · 03</span><h1 class="page-title">成员与权限</h1><p class="page-subtitle">${subtitle}</p></div></div>
    <section class="profile-panel"><div class="profile-copy"><strong>我的显示名称</strong><span>${config.authMode === "calendar" ? "修改后同步到工作日历；登录用户名和权限保持不变。" : "显示在工作台、项目卡片与项目工作区。"}</span></div><form id="profile-form" class="profile-form"><input name="username" maxlength="80" required value="${esc(state.me.username)}" aria-label="显示名称"><button class="button small" type="submit">保存名称</button></form></section>
    ${invitePanel}
    <table class="users-table"><thead><tr><th>${config.authMode === "calendar" ? "员工 / 登录用户名" : "NAS 用户"}</th><th>${config.authMode === "calendar" ? "统一账号" : "系统身份"}</th><th>审阅角色</th><th>审阅权限</th><th>最后使用</th></tr></thead><tbody>${state.users.map((user) => `<tr><td>${esc(user.username)}<br><span class="muted mono">${esc(user.login_username || user.nas_user_id)}</span></td><td>${config.authMode === "calendar" ? user.calendar_active ? "启用" : "已停用" : esc(user.system_type)}</td><td><select data-role-user="${esc(user.nas_user_id)}" ${user.nas_user_id === state.me.nas_user_id ? "disabled" : ""}>${Object.entries(roleLabels).map(([role,label]) => `<option value="${role}" ${user.role === role ? "selected" : ""}>${label}</option>`).join("")}</select></td><td><label class="check-row"><input data-active-user="${esc(user.nas_user_id)}" type="checkbox" ${user.active ? "checked" : ""} ${user.nas_user_id === state.me.nas_user_id ? "disabled" : ""}>允许进入</label></td><td>${formatDate(user.last_seen_at)}</td></tr>`).join("")}</tbody></table>
  </section>`;
}

function bindGlobal() {
  document.querySelector("#identity-logout")?.addEventListener("click", async () => {
    try {
      const response = await fetch(`${config.apiBase}/auth/logout`, { method: "POST", credentials: "same-origin", redirect: "manual" });
      if (!response.ok && response.type !== "opaqueredirect") throw new Error("退出失败，请稍后重试");
      window.location.assign(config.identityUrl);
    } catch (error) { toast(error.message, "error"); }
  });
  document.querySelector("#lan-accelerate")?.addEventListener("click", switchToLan);
  document.querySelectorAll("[data-nav]").forEach((button) => button.addEventListener("click", async () => {
    const next = button.dataset.nav;
    if (next === "review" && !state.currentProject && state.projects[0]) await openProject(state.projects[0].id, false);
    if (next === "admin") await Promise.all([refreshUsers(), refreshInvitations(), refreshEngineers()]);
    state.page = next;
    render();
    window.scrollTo({ top: 0, behavior: "auto" });
  }));
  document.querySelector("#notification-toggle")?.addEventListener("click", () => {
    state.notificationsOpen = !state.notificationsOpen;
    render();
  });
  document.querySelector("#notifications-read-all")?.addEventListener("click", async (event) => {
    event.stopPropagation();
    try {
      await api("/notifications/read-all", { method: "POST" });
      await refreshNotifications();
      render();
    } catch (error) { toast(error.message, "error"); }
  });
  document.querySelectorAll("[data-notification]").forEach((button) => button.addEventListener("click", async () => {
    try {
      await api(`/notifications/${button.dataset.notification}/read`, { method: "POST" });
      state.notificationsOpen = false;
      await refreshNotifications();
      if (button.dataset.notificationProject) await openProject(button.dataset.notificationProject);
      else render();
    } catch (error) { toast(error.message, "error"); }
  }));
  document.querySelectorAll("[data-close]").forEach((button) => button.addEventListener("click", () => {
    const dialog = button.closest("dialog");
    if (dialog?.id === "project-files-dialog") state.projectFilesOpen = false;
    if (dialog?.id === "upload-dialog" && !state.uploading) state.uploadMode = "new";
    dialog?.close();
  }));
  document.querySelector("#project-files-dialog")?.addEventListener("close", () => { state.projectFilesOpen = false; });
  document.querySelector("#upload-dialog")?.addEventListener("close", () => { if (!state.uploading) state.uploadMode = "new"; });
  bindDialogForms();
}

async function switchToLan(event) {
  const button = event.currentTarget;
  if (button.disabled) return;
  button.disabled = true;
  const label = button.querySelector("span");
  if (label) label.textContent = "正在连接…";
  try {
    const result = await api("/auth/lan-handoff", { method: "POST" });
    const destination = new URL(result.handoff_url);
    toast("身份已安全交接，正在切换到 NAS 局域网…");
    window.location.assign(destination.toString());
  } catch (error) {
    button.disabled = false;
    if (label) label.textContent = "局域网加速";
    toast(error.message, "error");
  }
}

function bindProjects() {
  document.querySelector("#show-deleted-projects")?.addEventListener("click", openDeletedProjects);
  document.querySelectorAll("[data-delete-project]").forEach((button) => button.addEventListener("click", () => deleteProject(button)));
  document.querySelector("#new-project")?.addEventListener("click", openNewProjectDialog);
  document.querySelector("#project-search")?.addEventListener("input", (event) => { state.search = event.target.value; render(); });
  document.querySelector("#status-filter")?.addEventListener("change", (event) => { state.statusFilter = event.target.value; render(); });
  document.querySelectorAll("[data-project]").forEach((card) => card.addEventListener("click", () => openProject(card.dataset.project)));
  document.querySelectorAll("[data-project-flag]").forEach((button) => button.addEventListener("click", () => updateProjectFlag(button)));
  document.querySelectorAll("[data-share-version]").forEach((button) => button.addEventListener("click", () => openShareDialog(button.dataset.shareVersion)));
}

async function deleteProject(button) {
  const project = state.projects.find((item) => item.id === button.dataset.deleteProject);
  if (!project || !window.confirm(`确定删除项目“${project.title}”？\n\n项目将从你和员工的工作台、日历待办中隐藏，交付链接暂停使用。\nNAS 文件、音频版本和批注全部保留，可在“已删除项目”中恢复。`)) return;
  button.disabled = true;
  try {
    await api(`/projects/${project.id}`, { method: "DELETE" });
    if (state.currentProject?.id === project.id) {
      resetAudioSource();
      state.currentProject = null;
      state.currentVersion = null;
    }
    await refreshProjects();
    await refreshNotifications();
    render();
    toast("项目已删除，文件已保留，可在“已删除项目”中恢复");
  } catch (error) {
    button.disabled = false;
    toast(error.message, "error");
  }
}

async function openDeletedProjects() {
  try {
    const items = await api("/deleted-projects");
    const dialog = document.querySelector("#deleted-projects-dialog");
    if (!dialog) return;
    dialog.querySelector("#deleted-projects-list").innerHTML = items.length ? items.map((item) => `<div class="deleted-project-row"><div><strong>${esc(item.title)}</strong><small>${esc(item.client_name || "未填写客户")} · ${formatDate(item.deleted_at)}</small></div><button class="button small secondary" data-restore-project="${item.id}" aria-label="恢复项目 ${esc(item.title)}">恢复项目</button></div>`).join("") : "<p>暂无已删除项目。</p>";
    dialog.querySelectorAll("[data-restore-project]").forEach((button) => button.addEventListener("click", async () => {
      button.disabled = true;
      try {
        await api(`/projects/${button.dataset.restoreProject}/restore`, { method: "POST" });
        await refreshProjects();
        await refreshNotifications();
        render();
        await openDeletedProjects();
        toast("项目已恢复，原有文件和审阅进度保持不变");
      } catch (error) { button.disabled = false; toast(error.message, "error"); }
    }));
    if (!dialog.open) dialog.showModal();
  } catch (error) { toast(error.message, "error"); }
}

async function updateProjectFlag(button) {
  const flag = button.dataset.projectFlag;
  const enabled = button.dataset.enabled === "true";
  if (!button.dataset.projectId || !["priority", "delivered"].includes(flag)) return;
  button.disabled = true;
  try {
    const updated = await api(`/projects/${button.dataset.projectId}/flags`, {
      method: "PATCH",
      body: { [flag]: !enabled },
    });
    state.projects = state.projects.map((project) => project.id === updated.id ? updated : project);
    if (state.currentProject?.id === updated.id) state.currentProject = updated;
    render();
    toast(flag === "priority"
      ? (!enabled ? "项目已标记为优先" : "已取消优先标记")
      : (!enabled ? "项目已标记为已交付" : "已取消已交付标记"));
  } catch (error) {
    button.disabled = false;
    toast(error.message, "error");
  }
}

async function openProject(projectId, navigate = true) {
  try {
    comparisonWaveformRequestId += 1;
    state.currentProject = await api(`/projects/${projectId}`);
    state.currentVersion = state.currentProject.versions[0] || null;
    state.comparisonEnabled = false;
    state.comparisonSource = null;
    state.comparisonWaveform = null;
    state.comparisonLoadingId = null;
    state.loudnessMatchEnabled = false;
    state.loudnessMatchLoadingId = null;
    state.loudnessMatchError = null;
    state.auditionVersionId = state.currentVersion?.id || null;
    state.selection = { start_ms: 0, end_ms: null };
    state.viewport = { zoom: 1, start_ms: 0 };
    state.lyricSelection = null;
    state.lyricSearchResults = [];
    state.lyricSearchTitle = "";
    state.lyricSearchArtist = "";
    state.previousResultsOpen = false;
    state.nasScans = { task: null, review: null, reference: null };
    state.projectFilesOpen = false;
    state.audioAnalysisLoadingId = null;
    state.audioAnalysisError = null;
    await loadWaveform();
    if (navigate) state.page = "review";
    render();
    void prefetchComparisonWaveforms();
    if (navigate) window.scrollTo({ top: 0, behavior: "auto" });
  } catch (error) { toast(error.message, "error"); }
}

async function refreshCurrentProject() {
  if (!state.currentProject) return;
  const comparisonId = state.comparisonSource?.id;
  state.currentProject = await api(`/projects/${state.currentProject.id}`);
  state.currentVersion = state.currentProject.versions[0] || null;
  state.comparisonSource = state.comparisonEnabled ? findAuditionSource(comparisonId) : null;
  if (!state.comparisonSource) state.comparisonWaveform = null;
  await refreshProjects();
  await Promise.all([
    loadWaveform(),
    state.comparisonSource ? loadPreviewWaveform(state.comparisonSource).then((waveform) => { state.comparisonWaveform = waveform; void promoteComparisonWaveform(state.comparisonSource); }) : Promise.resolve(),
  ]);
}

function applyProjectDetail(project, preferredVersionId = state.currentVersion?.id) {
  if (!project) return;
  state.currentProject = project;
  state.currentVersion = project.versions.find((version) => version.id === preferredVersionId) || project.versions[0] || null;
  if (state.comparisonSource) {
    state.comparisonSource = findAuditionSource(state.comparisonSource.id);
    if (!state.comparisonSource) state.comparisonWaveform = null;
  }
  const existing = state.projects.findIndex((item) => item.id === project.id);
  if (existing >= 0) state.projects.splice(existing, 1, project);
  else state.projects.unshift(project);
}

async function loadWaveform() {
  if (!state.currentVersion) {
    state.waveform = { duration_ms: 0, channel_count: 1, channels: [{ label: "M", peaks: [[0, 0]] }], peaks: [[0, 0]] };
    state.lyrics = null;
    resetAudioSource();
    return;
  }
  [state.waveform, state.lyrics] = await Promise.all([
    loadPreviewWaveform(state.currentVersion),
    api(`/versions/${state.currentVersion.id}/lyrics`),
  ]);
  state.activeLyricIndex = -1;
  state.lyricFocusIndex = 0;
  const auditionSource = auditionSources().find((source) => source.id === state.auditionVersionId) || state.currentVersion;
  state.auditionVersionId = auditionSource.id;
  if (audio.dataset.version !== auditionSource.id) {
    setAuditionAudioSource(auditionSource, 0);
  }
  void promoteCurrentWaveform(state.currentVersion);
}

async function promoteCurrentWaveform(version) {
  const projectId = state.currentProject?.id;
  const versionId = version?.id;
  if (!projectId || !versionId) return;
  try {
    const waveform = await loadFullWaveform(version);
    if (state.currentProject?.id !== projectId || state.currentVersion?.id !== versionId) return;
    state.waveform = waveform;
    drawWaveform();
  } catch { /* The compact preview remains usable; a later refresh can retry. */ }
}

async function promoteComparisonWaveform(source) {
  const projectId = state.currentProject?.id;
  const versionId = state.currentVersion?.id;
  const sourceId = source?.id;
  if (!projectId || !versionId || !sourceId) return;
  try {
    const waveform = await loadFullWaveform(source);
    if (!state.comparisonEnabled
      || state.currentProject?.id !== projectId
      || state.currentVersion?.id !== versionId
      || state.comparisonSource?.id !== sourceId) return;
    state.comparisonWaveform = waveform;
    drawWaveform();
  } catch { /* Keep the fast preview and retry after a later selection. */ }
}

async function prefetchComparisonWaveforms() {
  const projectId = state.currentProject?.id;
  const sources = nonCurrentAuditionSources();
  const reference = sources.find((source) => source.isReference);
  const ordered = [sources[0], reference, ...sources.slice(1)].filter(
    (source, index, all) => source && all.findIndex((item) => item.id === source.id) === index,
  );
  await Promise.allSettled(ordered.slice(0, 2).map((source) => loadPreviewWaveform(source)));
  for (const source of ordered.slice(2)) {
    if (state.currentProject?.id !== projectId) return;
    try { await loadPreviewWaveform(source); } catch { /* On-demand enable will retry. */ }
  }
}

function bindReview() {
  bindProjectFileWorkflow();
  document.querySelector("#open-project-files")?.addEventListener("click", openProjectFilesDialog);
  document.querySelector("[data-audio-analysis-retry]")?.addEventListener("click", (event) => {
    event.preventDefault();
    void ensureAudioAnalysis(true);
  });
  document.querySelector("#open-upload")?.addEventListener("click", () => openUploadDialog("new"));
  document.querySelector("#empty-upload")?.addEventListener("click", () => openUploadDialog("new"));
  document.querySelector("#open-replace-upload")?.addEventListener("click", () => openUploadDialog("replace"));
  document.querySelector("#open-reference-upload")?.addEventListener("click", openReferenceUploadDialog);
  if (!state.currentVersion) return;
  bindWorkspaceResizers();
  const canvas = document.querySelector("#waveform");
  if (resizeObserver) resizeObserver.disconnect();
  resizeObserver = new ResizeObserver(drawWaveform);
  resizeObserver.observe(canvas);
  bindCanvas(canvas);
  drawWaveform();
  document.querySelectorAll("[data-audition-version]").forEach((button) => button.addEventListener("click", () => selectIntegratedAudition(button.dataset.auditionVersion)));
  document.querySelector("[data-toggle-comparison]")?.addEventListener("click", toggleComparisonWaveform);
  document.querySelector("[data-toggle-loudness-match]")?.addEventListener("click", toggleLoudnessMatch);
  document.querySelector("#play")?.addEventListener("click", togglePlayback);
  document.querySelector("#volume")?.addEventListener("input", (event) => {
    auditionVolume = Number(event.target.value);
    applyAudioOutputGains();
  });
  document.querySelectorAll("[data-comment-form]").forEach((form) => form.addEventListener("submit", addComment));
  document.querySelector("#lyrics-search-form")?.addEventListener("submit", searchLyrics);
  document.querySelectorAll("[data-attach-lyric]").forEach((button) => button.addEventListener("click", () => attachLyrics(Number(button.dataset.attachLyric))));
  document.querySelectorAll("[data-lyric-seek]").forEach((button) => button.addEventListener("click", (event) => { event.stopPropagation(); selectLyricLine(Number(button.dataset.lyricSeek)); }));
  document.querySelectorAll(".lyric-line").forEach((line) => line.addEventListener("mouseup", () => captureLyricSelection(line)));
  document.querySelector('[data-select-lyric="line"]')?.addEventListener("click", () => selectLyricLine(state.lyricFocusIndex));
  document.querySelector('[data-select-lyric="paragraph"]')?.addEventListener("click", () => selectLyricParagraph(state.lyricFocusIndex));
  document.querySelector("[data-clear-lyric]")?.addEventListener("click", clearLyricSelection);
  document.querySelectorAll("[data-zoom]").forEach((button) => button.addEventListener("click", () => changeZoom(button.dataset.zoom)));
  document.querySelectorAll("[data-comment-seek]").forEach((button) => button.addEventListener("click", () => seekToComment(button.dataset.commentSeek)));
  document.querySelectorAll("[data-reason-form]").forEach((form) => form.addEventListener("submit", submitCommentReason));
  document.querySelectorAll("[data-toggle-reason]").forEach((button) => button.addEventListener("click", () => setReasonFormVisibility(button.dataset.toggleReason, true)));
  document.querySelectorAll("[data-cancel-reason]").forEach((button) => button.addEventListener("click", () => setReasonFormVisibility(button.dataset.cancelReason, false)));
  document.querySelectorAll("[data-comment-edit-form]").forEach((form) => form.addEventListener("submit", saveCommentEdit));
  document.querySelectorAll("[data-edit-comment]").forEach((button) => button.addEventListener("click", () => setCommentEditVisibility(button.dataset.editComment, true)));
  document.querySelectorAll("[data-cancel-comment-edit]").forEach((button) => button.addEventListener("click", () => setCommentEditVisibility(button.dataset.cancelCommentEdit, false)));
  document.querySelectorAll("[data-resolve]").forEach((button) => button.addEventListener("click", () => resolveComment(button.dataset.resolve, button.dataset.value === "1")));
  document.querySelectorAll("[data-delete-comment]").forEach((button) => button.addEventListener("click", () => deleteComment(button.dataset.deleteComment)));
  document.querySelector("[data-toggle-previous-results]")?.addEventListener("click", () => {
    state.previousResultsOpen = !state.previousResultsOpen;
    render();
  });
  document.querySelectorAll("[data-action]").forEach((button) => button.addEventListener("click", () => versionAction(button.dataset.action)));
  syncLyricsToPlayback();
  void ensureAudioAnalysis();
}

function openProjectFilesDialog() {
  if (!state.currentProject) return;
  state.projectFilesOpen = true;
  const dialog = document.querySelector("#project-files-dialog");
  if (dialog && !dialog.open) dialog.showModal();
}

async function ensureAudioAnalysis(force = false) {
  const version = state.currentVersion;
  if (!version || (!force && version.audio_analysis) || state.audioAnalysisLoadingId === version.id) return;
  const versionId = version.id;
  state.audioAnalysisLoadingId = versionId;
  state.audioAnalysisError = null;
  render();
  try {
    const analysis = await api(`/versions/${versionId}/audio-analysis${force ? "?refresh=true" : ""}`, { method: "POST", diagnosticName: "audio.analysis", diagnosticPhase: "processing" });
    const target = state.currentProject?.versions?.find((item) => item.id === versionId);
    if (target) target.audio_analysis = analysis;
    if (state.currentVersion?.id === versionId) state.currentVersion.audio_analysis = analysis;
  } catch (error) {
    state.audioAnalysisError = { versionId, message: error.message };
  } finally {
    if (state.audioAnalysisLoadingId === versionId) state.audioAnalysisLoadingId = null;
    render();
  }
}

function bindProjectFileWorkflow() {
  document.querySelector("#create-project-storage")?.addEventListener("click", createProjectStorage);
  document.querySelectorAll("[data-scan-nas]").forEach((button) => button.addEventListener("click", () => scanNasFolder(button.dataset.scanNas)));
  document.querySelector("#task-file-link-form")?.addEventListener("submit", linkTaskFiles);
  document.querySelector("#dispatch-task-form")?.addEventListener("submit", dispatchTask);
  document.querySelector("#review-file-associate-form")?.addEventListener("submit", associateReviewFile);
  document.querySelectorAll("[data-scan-reference]").forEach((button) => button.addEventListener("click", () => scanReferenceFolder(button.dataset.scanReference)));
  document.querySelector("#reference-file-associate-form")?.addEventListener("submit", associateReferenceFile);
}

async function createProjectStorage() {
  if (!state.currentProject || state.nasBusy) return;
  state.nasBusy = true;
  try {
    const project = await api(`/projects/${state.currentProject.id}/storage`, { method: "POST" });
    applyProjectDetail(project);
    render();
    toast("NAS 项目目录已建立");
  } catch (error) { toast(error.message, "error"); }
  finally { state.nasBusy = false; render(); }
}

async function scanNasFolder(kind) {
  if (!state.currentProject || state.nasBusy) return;
  state.nasBusy = true;
  const projectId = state.currentProject.id;
  try {
    const scan = await api(`/projects/${projectId}/nas-files?folder_kind=${encodeURIComponent(kind)}`);
    if (state.currentProject?.id !== projectId) return;
    state.nasScans[kind] = { ...scan, checkedAt: Date.now(), waiting: !scan.files.length };
    scheduleEmptyNasRetry(projectId, kind, kind, state.nasScans[kind]);
    render();
    toast(kind === "task" ? "已读取“任务文件”目录" : "已读取“待审阅”目录");
  } catch (error) { toast(error.message, "error"); }
  finally { state.nasBusy = false; render(); }
}

async function scanReferenceFolder(folderKind) {
  if (!state.currentProject || state.nasBusy) return;
  state.nasBusy = true;
  const projectId = state.currentProject.id;
  try {
    const scan = await api(`/projects/${projectId}/nas-files?folder_kind=${encodeURIComponent(folderKind)}`);
    if (state.currentProject?.id !== projectId) return;
    state.nasScans.reference = { ...scan, checkedAt: Date.now(), waiting: !scan.files.length };
    scheduleEmptyNasRetry(projectId, "reference", folderKind, state.nasScans.reference);
    render();
    toast(`已读取“${folderKind === "task" ? "任务文件" : "待审阅"}”中的原唱候选`);
  } catch (error) { toast(error.message, "error"); }
  finally { state.nasBusy = false; render(); }
}

function scheduleEmptyNasRetry(projectId, key, folderKind, scan, remaining = 18) {
  if (scan.files.length || !remaining) return;
  setTimeout(async () => {
    const stillCurrent = () => state.currentProject?.id === projectId && state.nasScans[key] === scan;
    if (!stillCurrent()) return;
    if (!state.projectFilesOpen || state.page !== "review") { scan.waiting = false; return; }
    if (state.nasBusy) { scheduleEmptyNasRetry(projectId, key, folderKind, scan, remaining - 1); return; }
    try {
      const result = await api(`/projects/${projectId}/nas-files?folder_kind=${encodeURIComponent(folderKind)}`);
      if (!stillCurrent() || !state.projectFilesOpen) return;
      const next = { ...result, checkedAt: Date.now(), waiting: !result.files.length && remaining > 1 };
      state.nasScans[key] = next;
      // Update only the empty result area, never reset notes or another folder's selection.
      const output = document.querySelector(`[data-nas-output="${key}"]`);
      if (output) output.innerHTML = (key === "reference" ? nasReferenceInnerHtml() : nasScanInnerHtml(key)) + nasScanTimeHtml(next);
      if (result.files.length) toast(`同步文件已出现，共 ${result.files.length} 个`);
      scheduleEmptyNasRetry(projectId, key, folderKind, next, remaining - 1);
    } catch (error) {
      if (!stillCurrent()) return;
      scan.waiting = false;
      const output = document.querySelector(`[data-nas-output="${key}"]`);
      if (output) output.innerHTML = `<div class="nas-scan-hint">${esc(error.message)}，请重新点击扫描。</div>`;
    }
  }, 10000);
}

async function linkTaskFiles(event) {
  event.preventDefault();
  if (!state.currentProject || state.nasBusy) return;
  const form = new FormData(event.currentTarget);
  const filenames = form.getAll("filename").map(String);
  if (!filenames.length) { toast("请先勾选要关联的任务文件。", "error"); return; }
  state.nasBusy = true;
  try {
    const project = await api(`/projects/${state.currentProject.id}/task-files/link`, { method: "POST", body: { filenames } });
    applyProjectDetail(project);
    state.nasScans.task = await api(`/projects/${state.currentProject.id}/nas-files?folder_kind=task`);
    render();
    toast(`已关联 ${filenames.length} 个任务文件`);
  } catch (error) { toast(error.message, "error"); }
  finally { state.nasBusy = false; render(); }
}

async function dispatchTask(event) {
  event.preventDefault();
  if (!state.currentProject || state.nasBusy) return;
  const form = new FormData(event.currentTarget);
  const engineerId = String(form.get("engineer_id") || "");
  if (!engineerId) { toast("请选择工程师。", "error"); return; }
  if (!window.confirm("确认发送任务？工程师工作台会立即收到未读提醒。")) return;
  state.nasBusy = true;
  try {
    const project = await api(`/projects/${state.currentProject.id}/dispatch`, { method: "POST", body: { engineer_id: engineerId } });
    applyProjectDetail(project);
    await Promise.all([refreshProjects(), refreshNotifications()]);
    render();
    toast(`任务已发送给 ${project.assigned_engineer?.username || "工程师"}`);
  } catch (error) { toast(error.message, "error"); }
  finally { state.nasBusy = false; render(); }
}

async function associateReviewFile(event) {
  event.preventDefault();
  if (!state.currentProject || state.nasBusy) return;
  const replacing = event.submitter?.dataset.associateMode === "replace";
  const replacementVersionId = replacing ? state.currentVersion?.id : null;
  if (replacing && !replacementVersionId) { toast("当前还没有可替换的审阅轮次。", "error"); return; }
  const form = new FormData(event.currentTarget);
  const filename = String(form.get("filename") || "");
  if (!filename) { toast("请选择一个待审阅音频。", "error"); return; }
  state.nasBusy = true;
  render();
  toast(replacing ? "正在替换当前轮次音频并重新生成波形，请稍候…" : "正在 NAS 内部复制并生成波形，请稍候…");
  try {
    const version = await api(`/projects/${state.currentProject.id}/review-files/associate`, {
      method: "POST",
      body: {
        filename,
        notes: String(form.get("notes") || ""),
        replace_version_id: replacementVersionId,
      },
      diagnosticName: replacing ? "nas-review.replace" : "nas-review.associate",
      diagnosticPhase: "processing",
      diagnosticBytes: state.nasScans.review?.files.find((file) => file.filename === filename)?.size_bytes,
    });
    applyProjectDetail(version.project, version.id);
    state.auditionVersionId = version.id;
    state.nasScans.review = null;
    if (replacing) {
      resetAudioSource();
      state.selection = { start_ms: 0, end_ms: null };
      state.viewport = { zoom: 1, start_ms: 0 };
    }
    await loadWaveform();
    render();
    toast(replacing
      ? `V${String(version.version_number).padStart(2, "0")} 音频已替换；轮次、批注和歌词保持不变。`
      : version.processing_error
      ? `原文件已关联，但音频处理失败：${version.processing_error}`
      : `V${String(version.version_number).padStart(2, "0")} 已关联，请检查后点击“提交审阅”。`,
    version.processing_error ? "error" : "info");
  } catch (error) { toast(error.message, "error"); }
  finally { state.nasBusy = false; render(); }
}

async function associateReferenceFile(event) {
  event.preventDefault();
  if (!state.currentProject || state.nasBusy) return;
  const scan = state.nasScans.reference;
  const form = new FormData(event.currentTarget);
  const filename = String(form.get("filename") || "");
  if (!scan || !filename) { toast("请先扫描并选择一份原唱音频。", "error"); return; }
  state.nasBusy = true;
  render();
  toast("正在关联原唱并生成试听文件与波形，请稍候…");
  try {
    const reference = await api(`/projects/${state.currentProject.id}/reference-files/associate`, {
      method: "POST",
      body: { filename, folder_kind: scan.folder_kind },
      diagnosticName: "nas-reference.associate",
      diagnosticPhase: "processing",
      diagnosticBytes: scan.files.find((file) => file.filename === filename)?.size_bytes,
    });
    applyProjectDetail(reference.project, state.currentVersion?.id);
    state.nasScans.reference = null;
    if (state.currentVersion) await selectAuditionVersion(state.currentVersion.id, false);
    render();
    toast(reference.processing_error
      ? `原唱文件已关联，但试听处理失败：${reference.processing_error}`
      : "原唱已关联，可在试听区直接切换对比",
    reference.processing_error ? "error" : "info");
  } catch (error) { toast(error.message, "error"); }
  finally { state.nasBusy = false; render(); }
}

function bindWorkspaceResizers() {
  const workspace = document.querySelector(".review-workspace");
  const upper = document.querySelector(".upper-workspace");
  if (!workspace || !upper) return;
  const applyRatio = (type, ratio, persist = false) => {
    if (type === "columns") {
      state.workspaceSplit.columns = clamp(ratio, .28, .72);
      workspace.style.setProperty("--lyrics-width", `${(state.workspaceSplit.columns * 100).toFixed(1)}%`);
      if (persist) localStorage.setItem("ale-review-lyrics-width", String(state.workspaceSplit.columns));
    } else {
      state.workspaceSplit.rows = clamp(ratio, .36, .68);
      workspace.style.setProperty("--upper-height", `${(state.workspaceSplit.rows * 100).toFixed(1)}%`);
      if (persist) localStorage.setItem("ale-review-upper-height", String(state.workspaceSplit.rows));
    }
    requestAnimationFrame(drawWaveform);
  };
  document.querySelectorAll("[data-resizer]").forEach((resizer) => {
    const type = resizer.dataset.resizer;
    const ratioFromPointer = (event) => {
      const rect = type === "columns" ? upper.getBoundingClientRect() : workspace.getBoundingClientRect();
      return type === "columns" ? (event.clientX - rect.left) / rect.width : (event.clientY - rect.top) / rect.height;
    };
    const finish = (event) => {
      if (!resizer.hasPointerCapture?.(event.pointerId)) return;
      applyRatio(type, ratioFromPointer(event), true);
      resizer.releasePointerCapture(event.pointerId);
      document.body.classList.remove("is-resizing");
      resizer.classList.remove("is-active");
    };
    resizer.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      resizer.setPointerCapture(event.pointerId);
      document.body.classList.add("is-resizing");
      resizer.classList.add("is-active");
      applyRatio(type, ratioFromPointer(event));
    });
    resizer.addEventListener("pointermove", (event) => {
      if (resizer.hasPointerCapture?.(event.pointerId)) applyRatio(type, ratioFromPointer(event));
    });
    resizer.addEventListener("pointerup", finish);
    resizer.addEventListener("pointercancel", finish);
    resizer.addEventListener("keydown", (event) => {
      const negative = type === "columns" ? event.key === "ArrowLeft" : event.key === "ArrowUp";
      const positive = type === "columns" ? event.key === "ArrowRight" : event.key === "ArrowDown";
      if (!negative && !positive) return;
      event.preventDefault();
      applyRatio(type, state.workspaceSplit[type === "columns" ? "columns" : "rows"] + (positive ? .025 : -.025), true);
    });
  });
}

function openUploadDialog(mode = "new") {
  if (!state.currentProject || !["engineer", "reviewer", "admin"].includes(state.me.role)) return;
  const replacing = mode === "replace";
  if (replacing && !state.currentVersion) return;
  if (!replacing && state.me.role === "engineer") {
    const missing = submissionCommentsMissingReason();
    if (missing.length) {
      const first = missing[0];
      render();
      requestAnimationFrame(() => {
        document.querySelector(`[data-comment="${first.id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        setReasonFormVisibility(first.id, true);
      });
      toast(`还有 ${missing.length} 条未解决批注缺少原因；补充后即可一次上传并自动提交。`, "error");
      return;
    }
  }
  state.uploadMode = replacing ? "replace" : "new";
  render();
  document.querySelector("#upload-form")?.reset();
  document.querySelector("#upload-progress")?.classList.add("hidden");
  document.querySelector("#upload-dialog").showModal();
}

function openReferenceUploadDialog() {
  if (!state.currentProject || !["engineer", "reviewer", "admin"].includes(state.me.role) || state.uploading) return;
  document.querySelector("#reference-form")?.reset();
  document.querySelector("#reference-upload-progress")?.classList.add("hidden");
  document.querySelector("#reference-dialog")?.showModal();
}

async function selectIntegratedAudition(sourceId) {
  if (!state.currentVersion) return;
  void ensureAudioGraph();
  if (state.comparisonEnabled && String(sourceId) !== String(state.currentVersion.id)) {
    void selectComparisonSource(sourceId);
  }
  await selectAuditionVersion(sourceId);
}

async function toggleComparisonWaveform() {
  if (!state.currentVersion) return;
  if (state.comparisonEnabled) {
    comparisonWaveformRequestId += 1;
    state.comparisonEnabled = false;
    state.comparisonSource = null;
    state.comparisonWaveform = null;
    state.comparisonLoadingId = null;
    state.loudnessMatchEnabled = false;
    state.loudnessMatchLoadingId = null;
    state.loudnessMatchError = null;
    render();
    applyAudioOutputGains();
    await selectAuditionVersion(state.currentVersion.id);
    toast("已关闭对比，恢复当前版本单轨。");
    return;
  }
  const active = findAuditionSource(state.auditionVersionId);
  const candidate = active && active.id !== state.currentVersion.id ? active : nonCurrentAuditionSources()[0];
  if (!candidate) return;
  state.comparisonEnabled = true;
  await selectComparisonSource(candidate.id);
}

async function selectComparisonSource(sourceId) {
  const candidate = findAuditionSource(sourceId);
  if (!candidate || candidate.id === state.currentVersion.id) return;
  const requestId = ++comparisonWaveformRequestId;
  const projectId = state.currentProject.id;
  const currentVersionId = state.currentVersion.id;
  state.comparisonSource = candidate;
  state.comparisonWaveform = {
    duration_ms: Number(candidate.duration_ms || state.waveform.duration_ms) || 1,
    channel_count: 1,
    channels: [{ label: "", peaks: [[0, 0]] }],
    peaks: [[0, 0]],
    pending: true,
  };
  state.comparisonLoadingId = candidate.id;
  state.loudnessMatchError = null;
  render();
  if (state.loudnessMatchEnabled) void ensureLoudnessMatchAnalyses(candidate);
  try {
    const waveform = await loadPreviewWaveform(candidate);
    if (requestId !== comparisonWaveformRequestId
      || !state.comparisonEnabled
      || state.currentProject?.id !== projectId
      || state.currentVersion?.id !== currentVersionId) return;
    state.comparisonWaveform = waveform;
    state.comparisonSource = candidate;
    state.comparisonLoadingId = null;
    render();
    void promoteComparisonWaveform(candidate);
    toast(`已选用${auditionSourceLabel(candidate)}做对比；播放位置保持不变。`);
  } catch (error) {
    if (requestId !== comparisonWaveformRequestId) return;
    state.comparisonEnabled = false;
    state.comparisonSource = null;
    state.comparisonWaveform = null;
    state.comparisonLoadingId = null;
    render();
    toast(error.message, "error");
  }
}

async function ensureSourceAudioAnalysis(source) {
  if (!source || source.audio_analysis) return source?.audio_analysis || null;
  const sourceId = source.id;
  state.loudnessMatchLoadingId = sourceId;
  state.loudnessMatchError = null;
  render();
  try {
    const endpoint = source.isReference
      ? `/projects/${state.currentProject.id}/reference/audio-analysis`
      : `/versions/${source.id}/audio-analysis`;
    const analysis = await api(endpoint, { method: "POST", diagnosticName: "audio.match-analysis", diagnosticPhase: "processing" });
    const target = findAuditionSource(sourceId);
    if (target) target.audio_analysis = analysis;
    source.audio_analysis = analysis;
    return analysis;
  } finally {
    if (state.loudnessMatchLoadingId === sourceId) state.loudnessMatchLoadingId = null;
  }
}

async function ensureLoudnessMatchAnalyses(source = state.comparisonSource) {
  if (!state.currentVersion || !source) return;
  try {
    await Promise.all([
      ensureSourceAudioAnalysis(state.currentVersion),
      ensureSourceAudioAnalysis(source),
    ]);
    if (loudnessMatchDb(state.currentVersion.audio_analysis, source.audio_analysis) === null) {
      state.loudnessMatchEnabled = false;
      state.loudnessMatchError = "当前音频或对比源无可用的综合响度数据。";
      toast(state.loudnessMatchError, "error");
    }
  } catch (error) {
    state.loudnessMatchEnabled = false;
    state.loudnessMatchError = error.message;
    toast(`响度 Match 分析失败：${error.message}`, "error");
  } finally {
    applyAudioOutputGains();
    render();
  }
}

async function toggleLoudnessMatch() {
  if (!state.comparisonEnabled || !state.comparisonSource) return;
  state.loudnessMatchEnabled = !state.loudnessMatchEnabled;
  state.loudnessMatchError = null;
  void ensureAudioGraph();
  render();
  if (state.loudnessMatchEnabled) await ensureLoudnessMatchAnalyses();
  else applyAudioOutputGains();
}

async function selectAuditionVersion(sourceId, shouldRender = true) {
  const source = findAuditionSource(sourceId);
  if (!source) return;
  const previousId = String(audio.dataset.version || state.auditionVersionId || state.currentVersion?.id || "");
  if (previousId === String(source.id)) return;
  const currentMs = audio.currentTime * 1000;
  state.auditionVersionId = source.id;
  if (shouldRender) render();
  try {
    const switched = await setAuditionAudioSource(source, currentMs);
    if (!switched && state.auditionVersionId === source.id) {
      state.auditionVersionId = audio.dataset.version || previousId;
      if (shouldRender) render();
    }
  } catch (error) {
    state.auditionVersionId = previousId;
    if (shouldRender) render();
    if (!isPlaybackAbortError(error)) toast(`切换试听失败：${error.message}`, "error");
  }
}

function clearPendingMetadataRestore() {
  if (!pendingMetadataRestore) return;
  pendingMetadataRestore.media.removeEventListener("loadedmetadata", pendingMetadataRestore.handler);
  pendingMetadataRestore = null;
}

function cancelCrossfade() {
  if (crossfadeFrame !== null) cancelAnimationFrame(crossfadeFrame);
  crossfadeFrame = null;
}

function waitForMediaMetadata(media) {
  if (media.readyState >= 1) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      media.removeEventListener("loadedmetadata", ready);
      media.removeEventListener("error", failed);
    };
    const ready = () => { cleanup(); resolve(); };
    const failed = () => {
      cleanup();
      reject(media.error || new Error("音频加载失败"));
    };
    media.addEventListener("loadedmetadata", ready, { once: true });
    media.addEventListener("error", failed, { once: true });
  });
}

async function ensureAudioGraph() {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return false;
  if (!audioContext) {
    audioContext = new AudioContextClass();
    [audio, standbyAudio].forEach((media) => {
      const sourceNode = audioContext.createMediaElementSource(media);
      const gainNode = audioContext.createGain();
      sourceNode.connect(gainNode).connect(audioContext.destination);
      audioGainNodes.set(media, gainNode);
      media.volume = 1;
    });
  }
  if (audioContext.state === "suspended") await audioContext.resume();
  applyAudioOutputGains();
  return true;
}

function mediaMatchGain(media) {
  if (!state.loudnessMatchEnabled || String(media.dataset.version || "") === String(state.currentVersion?.id || "")) return 1;
  const source = findAuditionSource(media.dataset.version);
  const matchDb = loudnessMatchDb(state.currentVersion?.audio_analysis, source?.audio_analysis);
  return matchDb === null ? 1 : decibelsToGain(matchDb);
}

function setMediaOutputGain(media, fadeGain = 1) {
  const gain = Math.max(0, auditionVolume * mediaMatchGain(media) * fadeGain);
  const gainNode = audioGainNodes.get(media);
  if (gainNode) {
    media.volume = 1;
    gainNode.gain.value = gain;
  } else {
    media.volume = clamp(gain, 0, 1);
  }
}

function applyAudioOutputGains() {
  setMediaOutputGain(audio);
  setMediaOutputGain(standbyAudio);
}

function beginCrossfade(outgoing, incoming, durationMs = 90) {
  cancelCrossfade();
  const startedAt = performance.now();
  const step = (now) => {
    const progress = clamp((now - startedAt) / durationMs, 0, 1);
    setMediaOutputGain(incoming, progress);
    setMediaOutputGain(outgoing, 1 - progress);
    if (progress < 1 && audio === incoming) {
      crossfadeFrame = requestAnimationFrame(step);
      return;
    }
    crossfadeFrame = null;
    outgoing.pause();
    setMediaOutputGain(outgoing);
    setMediaOutputGain(incoming);
  };
  crossfadeFrame = requestAnimationFrame(step);
}

async function seamlesslySwitchAudioSource(source, positionMs) {
  const requestId = ++audioSwitchRequestId;
  const outgoing = audio;
  const incoming = standbyAudio;
  const sourceId = String(source.id);
  cancelCrossfade();
  incoming.pause();
  setMediaOutputGain(incoming, 0);
  if (incoming.dataset.version !== sourceId || !incoming.src) {
    incoming.dataset.version = sourceId;
    incoming.src = source.preview_url;
    incoming.load();
  }
  await waitForMediaMetadata(incoming);
  if (requestId !== audioSwitchRequestId) return false;
  const requestedSeconds = Math.max(0, Number(positionMs) || 0) / 1000;
  const knownDuration = Math.max(0, Number(incoming.duration) || Number(source.duration_ms) / 1000 || requestedSeconds);
  try { incoming.currentTime = Math.min(requestedSeconds, knownDuration); } catch { /* metadata may still be settling */ }
  await incoming.play();
  if (requestId !== audioSwitchRequestId) {
    incoming.pause();
    setMediaOutputGain(incoming);
    return false;
  }
  audio = incoming;
  standbyAudio = outgoing;
  beginCrossfade(outgoing, incoming);
  drawWaveform();
  syncLyricsToPlayback();
  return true;
}

async function setAuditionAudioSource(source, positionMs = 0) {
  if (!audio.paused || playbackPending) {
    return seamlesslySwitchAudioSource(source, positionMs);
  }
  pausePlayback();
  clearPendingMetadataRestore();
  const changeId = ++audioSourceChangeId;
  const sourceId = String(source.id);
  audio.dataset.version = sourceId;
  audio.src = source.preview_url;
  audio.load();
  const restorePosition = () => {
    if (changeId !== audioSourceChangeId || audio.dataset.version !== sourceId) return;
    clearPendingMetadataRestore();
    const requestedMs = Math.max(0, Number(positionMs) || 0);
    const knownDurationMs = Math.max(0, Number(audio.duration) * 1000 || Number(source.duration_ms) || requestedMs);
    try { audio.currentTime = Math.min(requestedMs, knownDurationMs) / 1000; } catch { /* metadata is not ready yet */ }
    drawWaveform();
  };
  pendingMetadataRestore = { media: audio, handler: restorePosition };
  audio.addEventListener("loadedmetadata", restorePosition, { once: true });
  if (audio.readyState >= 1) restorePosition();
  return true;
}

function resetAudioSource() {
  pausePlayback();
  clearPendingMetadataRestore();
  audioSourceChangeId += 1;
  [audio, standbyAudio].forEach((media) => {
    media.removeAttribute("src");
    delete media.dataset.version;
    media.load();
  });
}

function pausePlayback() {
  playbackAttemptId += 1;
  audioSwitchRequestId += 1;
  playbackPending = false;
  cancelCrossfade();
  audio.pause();
  standbyAudio.pause();
  applyAudioOutputGains();
}

async function startPlayback() {
  if (playbackPending || !audio.paused) return;
  const attemptId = ++playbackAttemptId;
  playbackPending = true;
  try {
    await ensureAudioGraph();
    await audio.play();
  } catch (error) {
    if (attemptId === playbackAttemptId && !isPlaybackAbortError(error)) {
      toast(`无法播放：${error.message}`, "error");
    }
  } finally {
    if (attemptId === playbackAttemptId) playbackPending = false;
  }
}

function togglePlayback() {
  if (playbackPending || !audio.paused) pausePlayback();
  else void startPlayback();
}

function auditionSources() {
  const sources = [
    state.currentVersion,
    ...comparisonVersions(state.currentProject?.versions || [], state.currentVersion?.id),
  ].filter(Boolean);
  if (state.currentProject?.reference) {
    sources.push({
      ...state.currentProject.reference,
      id: `reference:${state.currentProject.reference.id}`,
      isReference: true,
    });
  }
  return sources;
}

function nonCurrentAuditionSources() {
  return auditionSources().filter((source) => source.id !== state.currentVersion?.id);
}

function findAuditionSource(sourceId) {
  if (!sourceId) return null;
  return auditionSources().find((source) => String(source.id) === String(sourceId)) || null;
}

function auditionSourceLabel(source) {
  return source?.isReference ? "原唱" : `历史 V${String(source?.version_number || 0).padStart(2, "0")}`;
}

function drawWaveform() {
  const canvas = document.querySelector("#waveform");
  if (!canvas || !state.currentVersion) return;
  const rect = canvas.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.floor(rect.width * dpr));
  const height = Math.max(1, Math.floor(rect.height * dpr));
  if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const w = rect.width;
  const h = rect.height;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = "#f6f8f9";
  ctx.fillRect(0, 0, w, h);
  const duration = timelineDuration();
  const view = normalizeViewport(state.viewport, duration);
  state.viewport = { zoom: view.zoom, start_ms: view.start_ms };
  const comparing = Boolean(state.comparisonEnabled && state.comparisonSource && state.comparisonWaveform);
  const roleVisibleComments = state.me.role === "engineer"
    ? engineerVisibleComments(state.currentProject.comments, state.currentVersion.id)
    : (state.currentProject.comments || []);
  const timedComments = roleVisibleComments.filter((comment) => !comment.parent_id && !isGlobalComment(comment));
  const annotationsFor = (versionId) => assignAnnotationLanes(sortCommentsByAudioTime(timedComments.filter((comment) => comment.version_id === versionId)).flatMap((comment) => {
    const rawStart = timeToX(comment.start_ms, w, view, duration);
    const hasRange = comment.end_ms !== null && comment.end_ms !== undefined && Number(comment.end_ms) > Number(comment.start_ms || 0);
    const rawEnd = hasRange ? timeToX(comment.end_ms, w, view, duration) : rawStart;
    if (Math.max(rawStart, rawEnd) < -8 || Math.min(rawStart, rawEnd) > w + 8) return [];
    const markerX = clamp(rawStart, 0, w);
    const endX = clamp(rawEnd, 0, w);
    return [{ id: comment.id, comment, color: commentVisualColor(comment.id), markerX, endX, hasRange, start: hasRange ? Math.min(markerX, endX) : markerX - 6, end: hasRange ? Math.max(markerX, endX) : markerX + 6 }];
  }), 5);
  const currentAnnotationLayout = annotationsFor(state.currentVersion.id);
  const historyAnnotationLayout = comparing && !state.comparisonSource.isReference ? annotationsFor(state.comparisonSource.id) : [];
  const currentLaneCount = currentAnnotationLayout.reduce((count, item) => Math.max(count, item.lane + 1), 0);
  const historyLaneCount = historyAnnotationLayout.reduce((count, item) => Math.max(count, item.lane + 1), 0);
  const annotationTop = 52;
  const annotationLaneHeight = 12;
  const rulerHeight = Math.max(67, annotationTop + Math.max(1, currentLaneCount) * annotationLaneHeight + 6);
  const waveTop = rulerHeight + 8;
  const waveBottom = h - 8;
  const waveHeight = waveBottom - waveTop;
  const trackGap = comparing ? Math.max(32, historyLaneCount * annotationLaneHeight + 14) : 0;
  const trackHeight = Math.max(1, comparing ? (waveHeight - trackGap) / 2 : waveHeight);
  const currentCenter = waveTop + trackHeight / 2;
  const historyCenter = currentCenter + trackHeight + trackGap;
  const currentTrackBottom = currentCenter + trackHeight / 2;
  const historyTrackTop = historyCenter - trackHeight / 2;
  state.commentAnnotations = [
    ...currentAnnotationLayout.map((annotation) => ({ ...annotation, y: annotationTop + annotation.lane * annotationLaneHeight, trackBottom: currentTrackBottom, trackKind: "current" })),
    ...historyAnnotationLayout.map((annotation) => ({ ...annotation, y: historyTrackTop - trackGap + 9 + annotation.lane * annotationLaneHeight, trackBottom: historyCenter + trackHeight / 2, trackKind: "history" })),
  ];
  const targetTickMs = view.visible_ms / Math.max(2, w / 110);
  const tickSteps = [100, 250, 500, 1000, 2000, 5000, 10000, 15000, 30000, 60000, 120000, 300000, 600000];
  const tickMs = tickSteps.find((step) => step >= targetTickMs) || tickSteps.at(-1);
  ctx.strokeStyle = "#e4e9ec";
  ctx.fillStyle = "#8a9298";
  ctx.lineWidth = 1;
  ctx.font = "9px JetBrains Mono, monospace";
  ctx.textBaseline = "top";
  for (let time = Math.ceil(view.start_ms / tickMs) * tickMs; time <= view.end_ms; time += tickMs) {
    const x = timeToX(time, w, view, duration);
    ctx.beginPath(); ctx.moveTo(x + .5, rulerHeight); ctx.lineTo(x + .5, h); ctx.stroke();
    ctx.fillText(formatTime(time, tickMs < 1000), x + 5, 8);
  }
  const activeAuditionId = state.auditionVersionId || state.currentVersion.id;
  const drawTrack = (waveform, center, height, version, label) => {
    const channels = waveformChannels(waveform);
    const ownDuration = Math.max(1, Number(waveform?.duration_ms || version?.duration_ms) || duration);
    const pxWidth = Math.max(1, Math.ceil(w));
    const active = activeAuditionId === version?.id;
    const channelGap = channels.length > 1 ? 5 : 0;
    const channelHeight = Math.max(1, (height - channelGap * (channels.length - 1)) / channels.length);
    channels.forEach((channel, channelIndex) => {
      const channelCenter = center - height / 2 + channelHeight / 2 + channelIndex * (channelHeight + channelGap);
      ctx.strokeStyle = "#d8dfe3";
      ctx.beginPath(); ctx.moveTo(0, channelCenter + .5); ctx.lineTo(w, channelCenter + .5); ctx.stroke();
      const peaks = channel.peaks;
      for (let x = 0; x < pxWidth; x += 1) {
        const timeA = xToTime(x, w, view, duration);
        if (timeA > ownDuration) continue;
        const timeB = Math.min(ownDuration, xToTime(x + 1, w, view, duration));
        const first = clamp(Math.floor((timeA / ownDuration) * peaks.length), 0, peaks.length - 1);
        const last = clamp(Math.ceil((timeB / ownDuration) * peaks.length), first + 1, peaks.length);
        let min = 0;
        let max = 0;
        for (let index = first; index < last; index += 1) {
          min = Math.min(min, Number(peaks[index]?.[0]) || 0);
          max = Math.max(max, Number(peaks[index]?.[1]) || 0);
        }
        const played = active && timeB <= audio.currentTime * 1000;
        ctx.strokeStyle = played ? "#00aec4" : active ? "#68747a" : "#a4adb2";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + .5, channelCenter + min * channelHeight * .41);
        ctx.lineTo(x + .5, channelCenter + max * channelHeight * .41);
        ctx.stroke();
      }
      ctx.fillStyle = active ? "#0292a5" : "#68747a";
      ctx.font = "700 9px JetBrains Mono, monospace";
      ctx.textBaseline = "top";
      const trackLabel = label ? `${label}${channels.length > 1 ? ` · ${channel.label}` : ""}` : channel.label;
      if (trackLabel && (label || channels.length > 1)) ctx.fillText(trackLabel, 7, channelCenter - channelHeight / 2 + 3);
    });
  };
  drawTrack(state.waveform, currentCenter, trackHeight, state.currentVersion, comparing ? `当前 V${String(state.currentVersion.version_number).padStart(2, "0")}` : "");
  if (comparing) {
    ctx.strokeStyle = "#cfd7db";
    ctx.setLineDash([3, 4]);
    ctx.beginPath(); ctx.moveTo(0, currentCenter + trackHeight / 2 + trackGap / 2); ctx.lineTo(w, currentCenter + trackHeight / 2 + trackGap / 2); ctx.stroke();
    ctx.setLineDash([]);
    const comparisonLabel = `${auditionSourceLabel(state.comparisonSource)}${state.comparisonWaveform.pending ? " · 波形加载中…" : ""}`;
    drawTrack(state.comparisonWaveform, historyCenter, trackHeight, state.comparisonSource, comparisonLabel);
  }
  const sx = timeToX(state.selection.start_ms, w, view, duration);
  const ex = timeToX(state.selection.end_ms ?? state.selection.start_ms, w, view, duration);
  if (Math.abs(ex - sx) > 2) {
    ctx.fillStyle = "rgba(0,174,196,.13)";
    ctx.fillRect(clamp(sx, 0, w), rulerHeight, clamp(ex, 0, w) - clamp(sx, 0, w), h - rulerHeight);
    ctx.strokeStyle = "#00aec4";
    ctx.lineWidth = 1;
    ctx.strokeRect(sx + .5, rulerHeight + .5, ex - sx - 1, h - rulerHeight - 1);
  } else if (sx >= 0 && sx <= w) {
    ctx.strokeStyle = "#00aec4";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(sx, rulerHeight); ctx.lineTo(sx, h); ctx.stroke();
  }
  state.commentAnnotations.forEach((annotation) => {
    const { comment, color, markerX, endX, hasRange, y, trackBottom } = annotation;
    ctx.save();
    ctx.globalAlpha = comment.resolved ? .22 : .72;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    if (shouldDrawAnnotationStem(hasRange, markerX, endX)) {
      ctx.beginPath();
      ctx.moveTo(markerX, y + 5);
      ctx.lineTo(markerX, trackBottom);
      ctx.stroke();
    }
    ctx.restore();
    ctx.save();
    ctx.globalAlpha = comment.resolved ? .38 : 1;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (hasRange && Math.abs(endX - markerX) > 3) {
      const leftX = Math.min(markerX, endX);
      const rightX = Math.max(markerX, endX);
      const bracketDepth = 9;
      const cornerRadius = Math.min(7, bracketDepth, (rightX - leftX) / 2);
      ctx.beginPath();
      ctx.moveTo(leftX, y + bracketDepth);
      ctx.lineTo(leftX, y + cornerRadius);
      ctx.quadraticCurveTo(leftX, y, leftX + cornerRadius, y);
      ctx.lineTo(rightX - cornerRadius, y);
      ctx.quadraticCurveTo(rightX, y, rightX, y + cornerRadius);
      ctx.lineTo(rightX, y + bracketDepth);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(markerX, y + 5);
      ctx.lineTo(markerX - 4, y - 2);
      ctx.lineTo(markerX + 4, y - 2);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  });
  const px = timeToX(audio.currentTime * 1000, w, view, duration);
  if (px >= 0 && px <= w) {
    ctx.strokeStyle = "#1a1d1f";
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(px, 0); ctx.lineTo(px, h); ctx.stroke();
    ctx.fillStyle = "#1a1d1f"; ctx.fillRect(px - 3, 0, 6, 5);
  }
  const range = document.querySelector("#viewport-range");
  if (range) range.textContent = `${formatTime(view.start_ms, true)}–${formatTime(view.end_ms, true)}`;
  const zoom = document.querySelector("#zoom-level");
  if (zoom) zoom.textContent = `${view.zoom.toFixed(view.zoom > 9 ? 0 : 1)}×`;
}

function bindCanvas(canvas) {
  const pointFromEvent = (event) => {
    const rect = canvas.getBoundingClientRect();
    return { x: clamp(event.clientX - rect.left, 0, rect.width), y: clamp(event.clientY - rect.top, 0, rect.height), width: rect.width };
  };
  canvas.addEventListener("pointerdown", (event) => {
    releaseEditorFocus(canvas);
    event.preventDefault();
    canvas.focus({ preventScroll: true });
    canvas.setPointerCapture(event.pointerId);
    const point = pointFromEvent(event);
    state.pointers.set(event.pointerId, point);
    if (state.pointers.size === 2) {
      const [a, b] = [...state.pointers.values()];
      const centerX = (a.x + b.x) / 2;
      const view = normalizeViewport(state.viewport, timelineDuration());
      state.gesture = { mode: "pinch", distance: Math.max(1, Math.abs(a.x - b.x)), centerX, anchorTime: xToTime(centerX, point.width, view, timelineDuration()), viewport: view };
      return;
    }
    state.lyricSelection = null;
    const pan = event.shiftKey || event.button === 1;
    state.gesture = { mode: pan ? "pan" : "select", id: event.pointerId, startX: point.x, currentX: point.x, viewport: normalizeViewport(state.viewport, timelineDuration()) };
    if (!pan) setSelectionFromTimes(xToTime(point.x, point.width, state.viewport, timelineDuration()), null, true);
  });
  canvas.addEventListener("pointermove", (event) => {
    const point = pointFromEvent(event);
    if (!state.pointers.has(event.pointerId)) { updateMarkerHover(event); return; }
    state.pointers.set(event.pointerId, point);
    if (state.gesture?.mode === "pinch" && state.pointers.size >= 2) {
      const [a, b] = [...state.pointers.values()];
      const distance = Math.max(1, Math.abs(a.x - b.x));
      const centerX = (a.x + b.x) / 2;
      const duration = timelineDuration();
      const zoom = clamp(state.gesture.viewport.zoom * distance / state.gesture.distance, 1, 64);
      const visible = duration / zoom;
      state.viewport = normalizeViewport({ zoom, start_ms: state.gesture.anchorTime - (centerX / point.width) * visible }, duration);
      drawWaveform();
      return;
    }
    if (!state.gesture || state.gesture.id !== event.pointerId) return;
    state.gesture.currentX = point.x;
    if (state.gesture.mode === "pan") {
      const deltaMs = ((state.gesture.startX - point.x) / point.width) * state.gesture.viewport.visible_ms;
      state.viewport = normalizeViewport({ zoom: state.gesture.viewport.zoom, start_ms: state.gesture.viewport.start_ms + deltaMs }, timelineDuration());
      drawWaveform();
    } else {
      setSelectionFromTimes(xToTime(state.gesture.startX, point.width, state.viewport, timelineDuration()), xToTime(point.x, point.width, state.viewport, timelineDuration()), true);
    }
  });
  const finishPointer = (event) => {
    const point = pointFromEvent(event);
    const wasPinch = state.gesture?.mode === "pinch";
    if (!wasPinch && state.gesture?.id === event.pointerId && state.gesture.mode === "select") {
      const startTime = xToTime(state.gesture.startX, point.width, state.viewport, timelineDuration());
      const endTime = xToTime(point.x, point.width, state.viewport, timelineDuration());
      setSelectionFromTimes(startTime, Math.abs(point.x - state.gesture.startX) >= 4 ? endTime : null, false);
      audio.currentTime = state.selection.start_ms / 1000;
    }
    state.pointers.delete(event.pointerId);
    if (wasPinch || state.pointers.size === 0) state.gesture = null;
  };
  canvas.addEventListener("pointerup", finishPointer);
  canvas.addEventListener("pointercancel", finishPointer);
  canvas.addEventListener("pointerleave", () => { if (!state.pointers.size) hideMarkerTooltip(); });
  canvas.addEventListener("wheel", (event) => {
    const point = pointFromEvent(event);
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      state.viewport = zoomAt(state.viewport, timelineDuration(), Math.exp(-event.deltaY * .01), point.x / point.width);
      drawWaveform();
    } else if (state.viewport.zoom > 1 && (Math.abs(event.deltaX) > 0 || event.shiftKey)) {
      event.preventDefault();
      const view = normalizeViewport(state.viewport, timelineDuration());
      const pixels = Math.abs(event.deltaX) > 0 ? event.deltaX : event.deltaY;
      state.viewport = normalizeViewport({ zoom: view.zoom, start_ms: view.start_ms + (pixels / point.width) * view.visible_ms }, timelineDuration());
      drawWaveform();
    }
  }, { passive: false });
}

function timelineDuration() {
  return Math.max(
    Number(state.waveform.duration_ms || state.currentVersion?.duration_ms) || 1,
    Number(state.comparisonWaveform?.duration_ms || state.comparisonSource?.duration_ms) || 1,
  );
}

function setSelectionFromTimes(a, b, transient) {
  const duration = timelineDuration();
  const start = Math.round(clamp(Math.min(a, b ?? a), 0, duration));
  const end = Math.round(clamp(Math.max(a, b ?? a), 0, duration));
  state.selection = { start_ms: start, end_ms: b !== null && end - start > 25 ? end : null };
  const label = state.selection.end_ms ? `区间 ${formatTime(start, true)}–${formatTime(state.selection.end_ms, true)}` : `点位 ${formatTime(start, true)}`;
  document.querySelector("#selection-chip")?.replaceChildren(document.createTextNode(label));
  document.querySelector("#anchor-label")?.replaceChildren(document.createTextNode(label));
  document.querySelector(".lyric-selection-preview")?.remove();
  drawWaveform();
  if (!transient && state.selection.end_ms) audio.currentTime = start / 1000;
}

function changeZoom(action) {
  if (action === "fit") state.viewport = { zoom: 1, start_ms: 0 };
  else state.viewport = zoomAt(state.viewport, timelineDuration(), action === "in" ? 2 : .5, .5);
  drawWaveform();
}

function updateMarkerHover(event) {
  const canvas = document.querySelector("#waveform");
  const tooltip = document.querySelector("#marker-tooltip");
  if (!canvas || !tooltip) return;
  const rect = canvas.getBoundingClientRect();
  const x = event.clientX - rect.left;
  const y = event.clientY - rect.top;
  let closest = null;
  let distance = 12;
  for (const annotation of state.commentAnnotations || []) {
    const nextDistance = Math.hypot(annotation.markerX - x, annotation.y - y);
    if (nextDistance < distance) { closest = annotation; distance = nextDistance; }
  }
  if (!closest) { hideMarkerTooltip(); return; }
  const parentRect = canvas.parentElement.getBoundingClientRect();
  tooltip.textContent = `${commentTimeLabel(closest.comment)} · ${closest.comment.lyric_text ? `“${closest.comment.lyric_text}” · ` : ""}${closest.comment.body}`;
  tooltip.style.setProperty("--comment-color", closest.color);
  tooltip.classList.add("visible");
  const markerTop = rect.top - parentRect.top + closest.y;
  const placeBelow = markerTop - 9 < tooltip.offsetHeight;
  tooltip.classList.toggle("below", placeBelow);
  tooltip.style.top = `${placeBelow ? markerTop + 9 : markerTop - 7}px`;
  const markerLeft = rect.left - parentRect.left + closest.markerX;
  const halfWidth = tooltip.offsetWidth / 2;
  const centerLeft = clamp(markerLeft, halfWidth + 7, parentRect.width - halfWidth - 7);
  tooltip.style.left = `${centerLeft}px`;
  tooltip.style.setProperty("--marker-arrow", `${clamp(halfWidth + markerLeft - centerLeft, 9, tooltip.offsetWidth - 9)}px`);
}

function hideMarkerTooltip() {
  document.querySelector("#marker-tooltip")?.classList.remove("visible");
}

async function searchLyrics(event) {
  event.preventDefault();
  const form = new FormData(event.currentTarget);
  const title = String(form.get("title") || "").trim();
  const artist = String(form.get("artist") || "").trim();
  const submit = event.currentTarget.querySelector('button[type="submit"]');
  if (!title || state.lyricSearchBusy) return;
  state.lyricSearchTitle = title;
  state.lyricSearchArtist = artist;
  state.lyricSearchBusy = true;
  submit.disabled = true;
  submit.textContent = "搜索中…";
  try {
    const result = await api(`/lyrics/search?title=${encodeURIComponent(title)}&artist=${encodeURIComponent(artist)}`);
    state.lyricSearchResults = result.results || [];
    state.lyricSearchBusy = false;
    render();
    toast(state.lyricSearchResults.length ? `找到 ${state.lyricSearchResults.length} 个同步 LRC` : "没有找到带时间戳的 LRC", state.lyricSearchResults.length ? "info" : "error");
  } catch (error) {
    state.lyricSearchBusy = false;
    submit.disabled = false;
    submit.textContent = "在线搜索 LRC";
    toast(error.message, "error");
  }
}

async function attachLyrics(index) {
  const candidate = state.lyricSearchResults[index];
  if (!candidate || !state.currentVersion) return;
  try {
    state.lyrics = await api(`/versions/${state.currentVersion.id}/lyrics`, { method: "PUT", body: candidate });
    state.lyricSearchResults = [];
    state.lyricSelection = null;
    state.lyricFocusIndex = 0;
    render();
    toast(`已关联 ${state.lyrics.lines.length} 行同步歌词`);
  } catch (error) { toast(error.message, "error"); }
}

function lyricRangeSelection(startIndex, endIndex, kind, exactText = "", characterRange = null) {
  const lines = state.lyrics?.lines || [];
  if (!lines[startIndex] || !lines[endIndex]) return;
  const start = Math.min(startIndex, endIndex);
  const end = Math.max(startIndex, endIndex);
  const duration = timelineDuration();
  const startMs = lines[start].time_ms;
  const endMs = lines[end].end_ms ?? Math.min(duration, lines[end].time_ms + 4000);
  const text = exactText || lines.slice(start, end + 1).map((line) => line.text).join(" / ");
  const pointOnly = kind === "word" && shouldUsePointMarker(text);
  state.lyricSelection = {
    startIndex: start,
    endIndex: end,
    charStart: characterRange?.start ?? null,
    charEnd: characterRange?.end ?? null,
    start_ms: startMs,
    end_ms: pointOnly ? null : endMs,
    text,
    kind,
    pointOnly,
  };
  state.lyricFocusIndex = start;
  state.selection = { start_ms: startMs, end_ms: pointOnly ? null : endMs };
  audio.currentTime = startMs / 1000;
  render();
}

function selectLyricLine(index) {
  lyricRangeSelection(index, index, "line");
}

function selectLyricParagraph(index) {
  const bounds = paragraphBounds(state.lyrics?.lines || [], index);
  if (bounds) lyricRangeSelection(bounds.start, bounds.end, "paragraph");
}

function captureLyricSelection(fallbackLine) {
  const selection = window.getSelection();
  const selectedRange = selection?.rangeCount ? selection.getRangeAt(0) : null;
  if (!selectedRange || !selection.toString().trim()) {
    selectLyricLine(Number(fallbackLine.dataset.lyricIndex));
    return;
  }
  const startElement = selectedRange.startContainer.nodeType === Node.ELEMENT_NODE ? selectedRange.startContainer : selectedRange.startContainer.parentElement;
  const endElement = selectedRange.endContainer.nodeType === Node.ELEMENT_NODE ? selectedRange.endContainer : selectedRange.endContainer.parentElement;
  const startLine = startElement?.closest?.(".lyric-line");
  const endLine = endElement?.closest?.(".lyric-line");
  if (!startLine || !endLine) return;
  const startText = startLine.querySelector(".lyric-text");
  const endText = endLine.querySelector(".lyric-text");
  if (!startText || !endText) return;
  const offsetWithin = (element, container, offset) => {
    const range = document.createRange();
    range.selectNodeContents(element);
    try { range.setEnd(container, offset); } catch { return 0; }
    return range.toString().length;
  };
  const startIndex = Number(startLine.dataset.lyricIndex);
  const endIndex = Number(endLine.dataset.lyricIndex);
  let charStart = offsetWithin(startText, selectedRange.startContainer, selectedRange.startOffset);
  let charEnd = offsetWithin(endText, selectedRange.endContainer, selectedRange.endOffset);
  const lines = state.lyrics?.lines || [];
  charStart = clamp(charStart, 0, lines[startIndex]?.text.length || 0);
  charEnd = clamp(charEnd, 0, lines[endIndex]?.text.length || 0);
  const selectedText = startIndex === endIndex
    ? lines[startIndex].text.slice(charStart, charEnd).trim()
    : [lines[startIndex].text.slice(charStart), ...lines.slice(startIndex + 1, endIndex).map((line) => line.text), lines[endIndex].text.slice(0, charEnd)].map((part) => part.trim()).filter(Boolean).join(" / ");
  if (!selectedText) return;
  lyricRangeSelection(startIndex, endIndex, "word", selectedText, { start: charStart, end: charEnd });
  selection.removeAllRanges();
}

function clearLyricSelection() {
  state.lyricSelection = null;
  render();
}

function syncLyricsToPlayback() {
  const lines = state.lyrics?.lines || [];
  const prompter = document.querySelector("#lyric-prompter");
  if (!prompter || !lines.length) return;
  const timeMs = audio.currentTime * 1000;
  const index = activeLyricIndex(lines, timeMs);
  prompter.querySelectorAll(".lyric-line").forEach((line, lineIndex) => {
    line.classList.toggle("is-current", lineIndex === index);
    line.classList.toggle("is-near", Math.abs(lineIndex - index) <= 2 && lineIndex !== index);
  });
  if (index < 0) return;
  state.activeLyricIndex = index;
  if (!state.lyricSelection) state.lyricFocusIndex = index;
  const current = prompter.querySelector(`.lyric-line[data-lyric-index="${index}"]`);
  if (!current) return;
  const next = prompter.querySelector(`.lyric-line[data-lyric-index="${index + 1}"]`);
  const prompterTop = prompter.getBoundingClientRect().top;
  const lineCenterWithinPrompter = (line) => {
    const rect = line.getBoundingClientRect();
    return rect.top - prompterTop + prompter.scrollTop + rect.height / 2;
  };
  let targetCenter = lineCenterWithinPrompter(current);
  if (next && lines[index + 1]) {
    const interval = Math.max(1, lines[index + 1].time_ms - lines[index].time_ms);
    const rawProgress = clamp((timeMs - lines[index].time_ms) / interval, 0, 1);
    const smoothProgress = rawProgress * rawProgress * (3 - 2 * rawProgress);
    const nextCenter = lineCenterWithinPrompter(next);
    targetCenter += (nextCenter - targetCenter) * smoothProgress;
  }
  prompter.scrollTop = clamp(targetCenter - prompter.clientHeight * .38, 0, Math.max(0, prompter.scrollHeight - prompter.clientHeight));
}

function bindAudioEvents(media) {
  media.addEventListener("timeupdate", () => {
    if (media !== audio) return;
    const timecode = document.querySelector("#timecode");
    if (timecode) timecode.textContent = formatTime(audio.currentTime * 1000, true);
    drawWaveform();
    syncLyricsToPlayback();
  });
  media.addEventListener("play", () => {
    if (media !== audio) return;
    const button = document.querySelector("#play");
    if (button) button.textContent = "❚❚";
  });
  media.addEventListener("pause", () => {
    if (media !== audio) return;
    const button = document.querySelector("#play");
    if (button) button.textContent = "▶";
  });
  media.addEventListener("loadedmetadata", () => { if (media === audio) drawWaveform(); });
}

[audio, standbyAudio].forEach(bindAudioEvents);

document.addEventListener("keydown", (event) => {
  if (event.code !== "Space" || event.repeat || state.page !== "review" || !state.currentVersion) return;
  const target = event.target;
  if (target instanceof HTMLElement && (target.matches("input, textarea, select") || target.isContentEditable)) return;
  if (document.querySelector("dialog[open]")) return;
  event.preventDefault();
  togglePlayback();
});

async function addComment(event) {
  event.preventDefault();
  const form = new FormData(event.currentTarget);
  const global = form.get("comment_scope") === "global";
  const supplement = isReviewSupplementOpen(state.currentProject, state.currentVersion);
  try {
    await api(`/versions/${state.currentVersion.id}/comments`, { method: "POST", body: {
      body: form.get("body"), start_ms: global ? null : state.selection.start_ms, end_ms: global ? null : state.selection.end_ms,
      lyric_start_index: global ? null : state.lyricSelection?.startIndex ?? null,
      lyric_end_index: global ? null : state.lyricSelection?.endIndex ?? null,
      lyric_char_start: global ? null : state.lyricSelection?.charStart ?? null,
      lyric_char_end: global ? null : state.lyricSelection?.charEnd ?? null,
      lyric_text: global ? null : state.lyricSelection?.text ?? null,
    } });
    if (!global) state.lyricSelection = null;
    await refreshCurrentProject();
    render();
    toast(supplement ? "补充批注已加入本次批次" : "批注已添加");
  } catch (error) { toast(error.message, "error"); }
}

async function submitCommentReason(event) {
  event.preventDefault();
  const form = new FormData(event.currentTarget);
  const commentId = event.currentTarget.dataset.reasonForm;
  const target = state.currentProject?.comments.find((comment) => comment.id === commentId);
  if (!target) return;
  try {
    await api(`/versions/${target.version_id}/comments`, { method: "POST", body: { body: form.get("body"), parent_id: commentId } });
    await refreshCurrentProject();
    render();
    toast("未解决原因已备注");
  } catch (error) { toast(error.message, "error"); }
}

async function seekToComment(commentId) {
  const comment = state.currentProject?.comments.find((item) => item.id === commentId);
  if (!comment || comment.start_ms === null || comment.start_ms === undefined) return;
  try {
    if (comment.version_id !== state.currentVersion?.id) {
      const version = state.currentProject.versions.find((item) => item.id === comment.version_id);
      if (!version) return;
      pausePlayback();
      state.comparisonEnabled = true;
      state.comparisonSource = version;
      state.comparisonWaveform = await loadPreviewWaveform(version);
      void promoteComparisonWaveform(version);
      state.auditionVersionId = state.currentVersion.id;
      state.viewport = { zoom: 1, start_ms: 0 };
      state.lyricSelection = null;
      render();
    }
    setSelectionFromTimes(Number(comment.start_ms), comment.end_ms === null ? null : Number(comment.end_ms), false);
    audio.currentTime = Number(comment.start_ms) / 1000;
    drawWaveform();
  } catch (error) { toast(error.message, "error"); }
}

function setReasonFormVisibility(commentId, open) {
  const form = document.querySelector(`[data-reason-form="${CSS.escape(commentId)}"]`);
  const toggle = document.querySelector(`[data-toggle-reason="${CSS.escape(commentId)}"]`);
  const row = form?.closest(".comment-card-row");
  if (!form || !toggle || !row) return;
  form.hidden = !open;
  toggle.hidden = open;
  row.classList.toggle("is-reason-editing", open);
  if (open) requestAnimationFrame(() => form.querySelector("input")?.focus());
}

function setCommentEditVisibility(commentId, open) {
  document.querySelectorAll("[data-comment-edit-form]").forEach((form) => {
    const active = open && form.dataset.commentEditForm === commentId;
    form.hidden = !active;
    form.closest(".comment-card")?.classList.toggle("is-editing", active);
    if (active) requestAnimationFrame(() => form.querySelector("textarea")?.focus());
  });
}

async function saveCommentEdit(event) {
  event.preventDefault();
  const commentId = event.currentTarget.dataset.commentEditForm;
  const form = new FormData(event.currentTarget);
  const supplement = isReviewSupplementOpen(state.currentProject, state.currentVersion);
  try {
    await api(`/comments/${commentId}`, { method: "PATCH", body: { body: form.get("body") } });
    await refreshCurrentProject();
    render();
    toast(supplement ? "批注修改已标记为补充，仍计本次批次" : "批注已保存");
  } catch (error) { toast(error.message, "error"); }
}

async function resolveComment(commentId, resolved) {
  if (state.pendingCommentIds.has(commentId)) return;
  const target = state.currentProject?.comments.find((comment) => comment.id === commentId);
  if (!target) return;
  const previous = { ...target };
  state.pendingCommentIds.add(commentId);
  target.resolved = resolved;
  state.currentProject.unresolved_comments = state.currentProject.comments.filter((comment) => !comment.parent_id && !comment.resolved).length;
  render();
  try {
    const updated = await api(`/comments/${commentId}`, {
      method: "PATCH",
      body: { resolved },
      diagnosticName: "comment.resolve",
      diagnosticPhase: "interaction",
    });
    Object.assign(target, previous, updated);
  } catch (error) {
    Object.assign(target, previous);
    toast(error.message, "error");
  } finally {
    state.pendingCommentIds.delete(commentId);
    state.currentProject.unresolved_comments = state.currentProject.comments.filter((comment) => !comment.parent_id && !comment.resolved).length;
    const summary = state.projects.find((project) => project.id === state.currentProject.id);
    if (summary) summary.unresolved_comments = state.currentProject.unresolved_comments;
    render();
  }
}

async function deleteComment(commentId) {
  if (!["reviewer", "admin"].includes(state.me.role)) return;
  if (!window.confirm("确认删除这条批注及其全部回复？")) return;
  try {
    await api(`/comments/${commentId}`, { method: "DELETE" });
    await refreshCurrentProject();
    render();
    toast("批注已删除");
  } catch (error) { toast(error.message, "error"); }
}

async function versionAction(action) {
  if (action === "share") { openShareDialog(state.currentVersion?.id); return; }
  if (action === "submit" && state.me.role === "engineer") {
    const missing = submissionCommentsMissingReason();
    if (missing.length) {
      const first = missing[0];
      render();
      requestAnimationFrame(() => {
        document.querySelector(`[data-comment="${first.id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        setReasonFormVisibility(first.id, true);
      });
      toast(`还有 ${missing.length} 条未解决批注缺少原因，已定位到第一条。`, "error");
      return;
    }
  }
  const map = { submit: "submit", changes: "request-changes", approve: "approve" };
  const promptText = action === "submit" ? "确认提交该版本给监制审阅？" : action === "changes" ? "确认将本轮批注意见提交给工程师修改？" : "确认批准这个精确版本用于交付？";
  if (!window.confirm(promptText)) return;
  let updatedProject = null;
  try {
    updatedProject = await api(`/versions/${state.currentVersion.id}/${map[action]}`, {
      method: "POST",
      body: { note: "", force: false },
      diagnosticName: `version.${map[action]}`,
      diagnosticPhase: "interaction",
    });
  } catch (error) {
    if (action === "approve" && error.status === 409 && window.confirm(`${error.message}\n仍然强制批准吗？`)) {
      updatedProject = await api(`/versions/${state.currentVersion.id}/approve`, {
        method: "POST",
        body: { note: "强制批准，保留未解决批注", force: true },
        diagnosticName: "version.approve.force",
        diagnosticPhase: "interaction",
      });
    } else { toast(error.message, "error"); return; }
  }
  applyProjectDetail(updatedProject);
  render();
  toast(action === "approve" ? "版本已批准，可生成交付链接" : action === "changes" ? "批注意见已提交给工程师修改" : "版本已提交给监制审阅");
}

function openShareDialog(versionId) {
  if (!versionId) return;
  state.shareVersionId = versionId;
  document.querySelector("#share-form")?.reset();
  document.querySelector("#share-result").innerHTML = "";
  document.querySelector("#share-dialog").showModal();
}

function bindAdmin() {
  document.querySelector("#profile-form")?.addEventListener("submit", updateProfile);
  document.querySelectorAll("[data-role-user]").forEach((select) => select.addEventListener("change", () => updateMember(select.dataset.roleUser)));
  document.querySelectorAll("[data-active-user]").forEach((checkbox) => checkbox.addEventListener("change", () => updateMember(checkbox.dataset.activeUser)));
  document.querySelector("#invite-form")?.addEventListener("submit", createInvitation);
  document.querySelector("#copy-invite")?.addEventListener("click", async () => {
    await navigator.clipboard.writeText(state.generatedInviteUrl);
    toast("个人入口链接已复制");
  });
  document.querySelectorAll("[data-regenerate-invite]").forEach((button) => button.addEventListener("click", async () => {
    if (!window.confirm(`重新生成${button.dataset.inviteName}的个人入口？旧入口会立即失效，成员身份和项目分配不会改变。`)) return;
    try {
      const result = await api(`/invitations/${button.dataset.regenerateInvite}/regenerate`, { method: "POST" });
      state.generatedInviteUrl = new URL(result.invite_url || result.invite_path, window.location.origin).toString();
      await Promise.all([refreshInvitations(), refreshUsers(), refreshEngineers(), refreshWorkflowDefaults()]);
      render();
      toast("新个人入口已生成，请复制给本人");
    } catch (error) { toast(error.message, "error"); }
  }));
  document.querySelectorAll("[data-revoke-invite]").forEach((button) => button.addEventListener("click", async () => {
    if (!window.confirm("撤销这个个人入口？撤销后该成员的现有会话也会失效。")) return;
    try {
      await api(`/invitations/${button.dataset.revokeInvite}`, { method: "DELETE" });
      await refreshInvitations();
      render();
      toast("个人入口已撤销");
    } catch (error) { toast(error.message, "error"); }
  }));
}

async function updateProfile(event) {
  event.preventDefault();
  const form = new FormData(event.currentTarget);
  try {
    state.me = await api("/me", { method: "PATCH", body: { username: String(form.get("username") || "").trim() } });
    await Promise.all([refreshUsers(), refreshEngineers(), refreshProjects(), refreshInvitations(), refreshWorkflowDefaults()]);
    if (state.currentProject) {
      const project = await api(`/projects/${state.currentProject.id}`);
      applyProjectDetail(project, state.currentVersion?.id);
    }
    render();
    toast("管理员名称已保存");
  } catch (error) { toast(error.message, "error"); }
}

async function createInvitation(event) {
  event.preventDefault();
  const form = new FormData(event.currentTarget);
  try {
    const result = await api("/invitations", { method: "POST", body: {
      username: form.get("username"),
      role: form.get("role"),
      expires_days: 30,
      permanent: true,
    } });
    state.generatedInviteUrl = new URL(result.invite_url || result.invite_path, window.location.origin).toString();
    await Promise.all([refreshInvitations(), refreshUsers(), refreshEngineers(), refreshWorkflowDefaults()]);
    render();
    toast("永久个人入口已生成");
  } catch (error) { toast(error.message, "error"); }
}

async function updateMember(userId) {
  const role = document.querySelector(`[data-role-user="${CSS.escape(userId)}"]`).value;
  const active = document.querySelector(`[data-active-user="${CSS.escape(userId)}"]`).checked;
  try { await api(`/users/${encodeURIComponent(userId)}`, { method: "PATCH", body: { role, active } }); await Promise.all([refreshUsers(), refreshEngineers(), refreshWorkflowDefaults()]); render(); toast("成员权限已保存"); }
  catch (error) { toast(error.message, "error"); await refreshUsers(); render(); }
}

function openNewProjectDialog() {
  const dialog = document.querySelector("#project-dialog");
  if (!dialog || state.uploading) return;
  state.newProjectFile = null;
  state.newProjectReferenceFile = null;
  state.pendingNewProjectId = null;
  document.querySelector("#project-form")?.reset();
  document.querySelector("#project-upload-progress")?.classList.add("hidden");
  const submit = document.querySelector("#project-submit");
  if (submit) submit.disabled = false;
  updateNewProjectFileDisplay();
  updateNewProjectReferenceDisplay();
  dialog.showModal();
}

function updateNewProjectFileDisplay() {
  const drop = document.querySelector("#project-audio-drop");
  const title = document.querySelector("#project-audio-title");
  const meta = document.querySelector("#project-audio-meta");
  if (!drop || !title || !meta) return;
  const file = state.newProjectFile;
  drop.classList.toggle("has-file", Boolean(file));
  title.textContent = file ? file.name : "拖入音频，或点击选择";
  meta.textContent = file ? `${formatBytes(file.size)} · 已选择，可继续填写项目信息` : "可选 · WAV / AIFF / FLAC / MP3 / M4A 等";
  const submit = document.querySelector("#project-submit");
  if (submit) submit.textContent = file
    ? (state.me.role === "engineer" ? "建立并提交监制审阅" : "建立并进入审阅")
    : "建立项目目录";
}

function chooseNewProjectFile(file) {
  if (!(file instanceof File) || !file.size) return;
  if (!isSupportedAudioFile(file)) {
    toast("请选择 WAV、AIFF、FLAC、MP3、M4A、AAC 或 OGG 音频文件。", "error");
    return;
  }
  state.newProjectFile = file;
  const title = document.querySelector("#project-title");
  if (title && !title.value.trim()) title.value = file.name.replace(/\.[^.]+$/, "");
  updateNewProjectFileDisplay();
}

function isSupportedAudioFile(file) {
  const suffix = file?.name?.includes(".") ? `.${file.name.split(".").pop().toLowerCase()}` : "";
  return [".wav", ".wave", ".aif", ".aiff", ".flac", ".mp3", ".m4a", ".aac", ".ogg"].includes(suffix);
}

function updateNewProjectReferenceDisplay() {
  const name = document.querySelector("#project-reference-name");
  if (!name) return;
  const file = state.newProjectReferenceFile;
  name.textContent = file ? `${file.name} · ${formatBytes(file.size)}` : "未选择";
  name.classList.toggle("has-file", Boolean(file));
}

function chooseNewProjectReferenceFile(file) {
  if (!(file instanceof File) || !file.size) {
    state.newProjectReferenceFile = null;
    updateNewProjectReferenceDisplay();
    return;
  }
  if (!isSupportedAudioFile(file)) {
    toast("原唱参考请选择 WAV、AIFF、FLAC、MP3、M4A、AAC 或 OGG 文件。", "error");
    return;
  }
  state.newProjectReferenceFile = file;
  updateNewProjectReferenceDisplay();
}

function bindNewProjectAudioPicker() {
  const drop = document.querySelector("#project-audio-drop");
  const input = document.querySelector("#project-audio-input");
  const referenceInput = document.querySelector("#project-reference-input");
  if (!drop || !input) return;
  updateNewProjectFileDisplay();
  updateNewProjectReferenceDisplay();
  referenceInput?.addEventListener("change", () => chooseNewProjectReferenceFile(referenceInput.files?.[0]));
  drop.addEventListener("click", (event) => {
    if (event.target !== input) input.click();
  });
  drop.addEventListener("keydown", (event) => {
    if (!["Enter", " "].includes(event.key)) return;
    event.preventDefault();
    input.click();
  });
  input.addEventListener("change", () => chooseNewProjectFile(input.files?.[0]));
  for (const type of ["dragenter", "dragover"]) {
    drop.addEventListener(type, (event) => {
      event.preventDefault();
      if (!state.uploading) drop.classList.add("is-dragging");
    });
  }
  for (const type of ["dragleave", "drop"]) {
    drop.addEventListener(type, (event) => {
      event.preventDefault();
      drop.classList.remove("is-dragging");
    });
  }
  drop.addEventListener("drop", (event) => {
    if (!state.uploading) chooseNewProjectFile(event.dataTransfer?.files?.[0]);
  });
}

async function uploadAudioFile(projectId, file, { notes = "", submitForReview = false, kind = "version", replaceVersionId = null, progress }) {
  const bar = progress.querySelector("i");
  const note = progress.querySelector(".progress-note");
  const uploadStarted = performance.now();
  progress.classList.remove("hidden");
  bar.style.width = "0%";
  const operation = kind === "reference" ? "reference" : replaceVersionId ? "version-replace" : "version";
  note.textContent = kind === "reference" ? "正在建立原唱上传会话…" : replaceVersionId ? "正在建立当前轮次替换会话…" : "正在建立安全上传会话…";
  const session = await api(`/projects/${projectId}/uploads`, {
    method: "POST",
    body: {
      filename: file.name,
      size_bytes: file.size,
      content_type: file.type || "application/octet-stream",
      notes,
      submit_for_review: submitForReview,
      kind,
      replace_version_id: replaceVersionId,
    },
    diagnosticName: `${operation}.upload.create_session`,
    diagnosticPhase: "upload",
  });
  const usingDirectChannel = session.upload_transport === "direct";
  note.textContent = usingDirectChannel ? "已连接 NAS 上传专用通道…" : "正在通过当前网关上传…";
  let offset = session.offset_bytes;
  const minimumChunkSize = 4 * 1024 * 1024;
  let chunkSize = 16 * 1024 * 1024;
  let consecutiveFailures = 0;
  while (offset < file.size) {
    const chunk = file.slice(offset, Math.min(file.size, offset + chunkSize));
    try {
      const response = await directUploadRequest(session, {
        method: "PATCH",
        offset,
        body: chunk,
        diagnosticName: `${operation}.upload.chunk`,
        diagnosticBytes: chunk.size,
        diagnosticAttempt: consecutiveFailures + 1,
      });
      const confirmedOffset = Number(response.headers.get("Upload-Offset"));
      offset = Number.isFinite(confirmedOffset) && confirmedOffset > offset ? confirmedOffset : offset + chunk.size;
      consecutiveFailures = 0;
    } catch (error) {
      let confirmedOffset = offset;
      try {
        const inspection = await directUploadRequest(session, {
          method: "HEAD",
          diagnosticName: `${operation}.upload.resume_check`,
        });
        confirmedOffset = Number(inspection.headers.get("Upload-Offset")) || offset;
      } catch { /* retry from the last locally confirmed offset */ }
      if (confirmedOffset > offset) {
        offset = confirmedOffset;
        consecutiveFailures = 0;
      } else {
        consecutiveFailures += 1;
        if (consecutiveFailures >= 3) throw error;
        chunkSize = Math.max(minimumChunkSize, Math.floor(chunkSize / 2));
        note.textContent = `网络响应中断，正在从 ${formatBytes(offset)} 自动续传…`;
        await new Promise((resolve) => setTimeout(resolve, consecutiveFailures * 900));
        continue;
      }
    }
    const percent = Math.round((offset / file.size) * 100);
    const elapsedSeconds = Math.max(.001, (performance.now() - uploadStarted) / 1000);
    const averageRate = offset / elapsedSeconds;
    bar.style.width = `${percent}%`;
    note.textContent = `${percent}% · ${formatBytes(offset)} / ${formatBytes(file.size)} · 平均 ${formatBytes(averageRate)}/s`;
  }
  note.textContent = kind === "reference"
    ? "原唱上传完成，正在生成试听文件…"
    : replaceVersionId
      ? "上传完成，正在替换当前轮次音频并重新生成波形…"
    : submitForReview
      ? "上传完成，正在生成校验值、试听文件和波形；完成后自动提交审阅…"
      : "上传完成，正在生成校验值、试听文件和波形…";
  return api(`/uploads/${session.id}/complete`, {
    method: "POST",
    diagnosticName: `${operation}.upload.process_audio`,
    diagnosticPhase: "processing",
    diagnosticBytes: file.size,
  });
}

function bindDialogForms() {
  bindNewProjectAudioPicker();
  document.querySelector("#project-form")?.addEventListener("submit", createProjectWithAudio);
  document.querySelector("#upload-form")?.addEventListener("submit", uploadVersion);
  document.querySelector("#reference-form")?.addEventListener("submit", uploadProjectReference);
  document.querySelector("#share-form")?.addEventListener("submit", createShare);
}

async function createProjectWithAudio(event) {
  event.preventDefault();
  if (state.uploading) return;
  const form = new FormData(event.currentTarget);
  const submit = document.querySelector("#project-submit");
  state.uploading = true;
  submit.disabled = true;
  try {
    let projectDetail;
    if (state.pendingNewProjectId) {
      projectDetail = await api(`/projects/${state.pendingNewProjectId}`);
    } else {
      projectDetail = await api("/projects", { method: "POST", body: {
        title: String(form.get("title") || "").trim(),
        client_name: String(form.get("client_name") || "").trim(),
        description: String(form.get("description") || "").trim(),
      } });
      state.pendingNewProjectId = projectDetail.id;
    }
    let version = null;
    if (state.newProjectFile) {
      version = await uploadAudioFile(projectDetail.id, state.newProjectFile, {
        notes: String(form.get("description") || "").trim(),
        submitForReview: true,
        progress: document.querySelector("#project-upload-progress"),
      });
      projectDetail = version.project || projectDetail;
    }
    document.querySelector("#project-dialog").close();
    applyProjectDetail(projectDetail, version?.id || null);
    state.currentVersion = version
      ? projectDetail.versions.find((item) => item.id === version.id) || projectDetail.versions[0] || null
      : null;
    state.nasScans = { task: null, review: null, reference: null };
    state.page = "review";
    state.pendingNewProjectId = null;
    state.newProjectFile = null;
    // The create response is authoritative. Show the saved project before
    // background refreshes, whose response bodies can fail independently.
    render();
    toast(version
      ? "项目已建立，音频已上传到 NAS 并提交监制审阅。"
      : state.me.role === "engineer"
        ? "项目目录已建立；把成品放入“待审阅”，关联后即可提交监制。"
        : "项目目录已建立；请把素材放入“任务文件”并完成关联。", "info");
    const refreshResults = await Promise.allSettled([refreshProjects(), refreshNotifications()]);
    syncNotificationButton();
    if (refreshResults.some((result) => result.status === "rejected")) {
      toast("项目已建立并保留；列表或提醒暂未刷新，稍后刷新页面即可，无需重复创建。", "info");
    }
  } catch (error) {
    toast(state.pendingNewProjectId
      ? `${error.message} 项目目录已保留，可直接重试上传。`
      : error.message, "error");
  } finally {
    state.uploading = false;
    if (submit) submit.disabled = false;
  }
}

async function uploadVersion(event) {
  event.preventDefault();
  if (state.uploading) return;
  const form = new FormData(event.currentTarget);
  const file = form.get("file");
  if (!(file instanceof File) || !file.size) return;
  const replacing = state.uploadMode === "replace";
  const replacementVersionId = replacing ? state.currentVersion?.id : null;
  if (replacing && !replacementVersionId) { toast("当前还没有可替换的审阅轮次。", "error"); return; }
  const progress = document.querySelector("#upload-progress");
  document.querySelector("#upload-submit").disabled = true;
  state.uploading = true;
  try {
    const version = await uploadAudioFile(state.currentProject.id, file, {
      notes: form.get("notes") || "",
      submitForReview: !replacing,
      replaceVersionId: replacementVersionId,
      progress,
    });
    if (version.processing_error) toast(`原始文件已保留，但音频处理失败：${version.processing_error}`, "error");
    document.querySelector("#upload-dialog").close();
    if (version.project) {
      applyProjectDetail(version.project, version.id);
      state.auditionVersionId = version.id;
      if (replacing) {
        resetAudioSource();
        state.selection = { start_ms: 0, end_ms: null };
        state.viewport = { zoom: 1, start_ms: 0 };
      }
      await loadWaveform();
    } else {
      await refreshCurrentProject();
      state.currentVersion = state.currentProject.versions.find((item) => item.id === version.id) || state.currentProject.versions[0];
    }
    state.uploadMode = "new";
    render();
    toast((replacing
      ? `V${String(version.version_number).padStart(2, "0")} 音频已替换；没有增加轮次，原批注继续保留。`
      : version.processing_error
      ? `V${String(version.version_number).padStart(2, "0")} 已保存，但处理失败，尚未提交`
      : `V${String(version.version_number).padStart(2, "0")} 上传完成，已自动提交监制审阅`) + exportNotice(version));
  } catch (error) { toast(error.message, "error"); }
  finally { state.uploading = false; const submit = document.querySelector("#upload-submit"); if (submit) submit.disabled = false; }
}

async function uploadProjectReference(event) {
  event.preventDefault();
  if (state.uploading || !state.currentProject) return;
  const form = new FormData(event.currentTarget);
  const file = form.get("file");
  if (!(file instanceof File) || !file.size) return;
  const progress = document.querySelector("#reference-upload-progress");
  const submit = document.querySelector("#reference-submit");
  state.uploading = true;
  if (submit) submit.disabled = true;
  try {
    const reference = await uploadAudioFile(state.currentProject.id, file, { kind: "reference", progress });
    if (reference.project) applyProjectDetail(reference.project, state.currentVersion?.id);
    if (reference.processing_error) toast(`原唱文件已保留，但试听处理失败：${reference.processing_error}`, "error");
    document.querySelector("#reference-dialog")?.close();
    await selectAuditionVersion(state.currentVersion.id, false);
    render();
    toast("原唱参考已上传，可在试听区直接切换对比" + exportNotice(reference));
  } catch (error) { toast(error.message, "error"); }
  finally { state.uploading = false; if (submit) submit.disabled = false; }
}

async function createShare(event) {
  event.preventDefault();
  const form = new FormData(event.currentTarget);
  const maxAccesses = form.get("max_accesses");
  const expiry = form.get("expiry_days");
  const versionId = state.shareVersionId || state.currentVersion?.id;
  if (!versionId) return;
  try {
    const result = await api(`/versions/${versionId}/delivery-links`, { method: "POST", body: { expiry_days: expiry === "permanent" ? null : Number(expiry), password: form.get("password") || "", allow_download: form.get("allow_download") === "on", max_accesses: maxAccesses ? Number(maxAccesses) : null } });
    const fullUrl = new URL(result.url, window.location.origin).toString();
    document.querySelector("#share-result").innerHTML = `<div class="link-result"><input id="generated-link" value="${esc(fullUrl)}" readonly><button id="copy-link" type="button" class="button secondary">复制</button></div><p class="muted">链接只显示这一次；遗失后可撤销并重新生成。</p>`;
    document.querySelector("#copy-link").addEventListener("click", async () => { await navigator.clipboard.writeText(fullUrl); toast("交付链接已复制"); });
  } catch (error) { toast(error.message, "error"); }
}

async function boot() {
  try {
    await initIdentity();
    await Promise.all([refreshProjects(), refreshUsers(), refreshEngineers(), refreshInvitations(), refreshWorkflowDefaults(), refreshNotifications()]);
    const requestedProjectId = new URLSearchParams(window.location.search).get("project");
    if (requestedProjectId) await openProject(requestedProjectId);
    else render();
    startNotificationPolling();
  } catch (error) {
    if (config.authMode === "calendar" && error.status === 401) {
      const project = new URLSearchParams(window.location.search).get("project");
      window.location.replace(`/auth/login${project ? `?project=${encodeURIComponent(project)}` : ""}`);
      return;
    }
    const help = config.authMode === "calendar" ? `<a href="/auth/login">使用工作日历账号重新登录</a>；如仍无法进入，请联系管理员检查账号与审阅权限。` : config.authMode === "invite" ? "请打开管理员发给你的个人入口链接；管理员本人请使用保管的管理员入口链接。" : "请确认从 UGOS Pro NAS 桌面打开应用，并且管理员已经完成应用授权。";
    document.querySelector("#app").innerHTML = `<div class="error-card"><strong>工作台无法启动</strong><p>${esc(error.message)}</p><p>${help}</p></div>`;
  }
}

boot();
